import { PublicProfile } from './PublicProfile.js';
import { Guides, AutumnGuide, TestingScentsGuide } from './Guides.js';
import { EducationGuide } from './EducationGuides.js';
import { educationGuides, guidePath, type EducationGuideSlug } from '../../../packages/shared/src/guides.js';
import { FragranceReviews } from './components/FragranceReviews.js';
import { reviewsVisible } from '../../../packages/shared/src/reviews.js';
import { defaultSearchSort } from '../../../packages/shared/src/search.js';
import { EthicalTags, ethicalFields } from './components/EthicalTags.js';
import { FilterSelect } from './components/FilterSelect.js';
import { familyHubs, noteHubs, discoveryPath, type DiscoveryHub } from '../../../packages/shared/src/discovery.js';
import { CountryCombobox } from './components/CountryCombobox.js';
import { defaultCountry } from './country-default.js';
import { FinderFilters } from './components/FinderFilters.js';
import { BuyingLinks, buyingLinkTitle } from './components/BuyingLinks.js';
import { FragrancePrices } from './components/FragrancePrices.js';
import { PopularComparisonCoverflow } from './components/PopularComparisonCoverflow.js';
import { verifiedFragranceNotes } from '../../../packages/shared/src/verified-fragrance-notes.js';
import { isChanelFragrance } from './chanel.js';
import { ChanelDisclosure } from './components/ChanelDisclosure.js';
import { OfficialStoreBadge, VerificationBadge } from './components/VerificationBadge.js';
import { OfferPromotion } from './components/OfferPromotion.js';
import { ComparisonDetails } from './components/ComparisonDetails.js';
import {
  canonicalAccord,
  compareAccords,
  FragranceScentRatings,
  performanceSteps,
  type ComparisonProps,
} from './components/ScentProfile.js';
import {
  accordFacet,
  comparativePhrase,
  comparisonRowPhrase,
  supported as profileSupported,
} from './components/decisionSummary.js';
import { BrandLogo } from './components/BrandLogo.js';
import { BackToTop } from './components/BackToTop.js';
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Menu,
  ChevronDown,
  Search,
  ArrowUp,
  ArrowDown,
  Equal,
  Check,
  Info,
  X,
  Mars,
  Venus,
  VenusAndMars,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
// Phosphor for the plate badges: it carries the vocabulary this catalogue actually needs - a droplet
// for a note's lift, a target for the nearest profile - where a general-purpose set only offered
// sparkles, and its weighted strokes hold their shape at badge size.
// Imported by subpath rather than from the barrel: the package's root index re-exports every icon
// with extensionless specifiers, which this project's NodeNext resolution cannot follow, and the
// direct paths keep the other nine thousand glyphs out of the bundle.
import { DropIcon } from '@phosphor-icons/react/dist/csr/Drop';
import { TagIcon } from '@phosphor-icons/react/dist/csr/Tag';
import { api, useResource } from './api.js';
import {
  AdminBar,
  AdminContext,
  DeleteRecord,
  EditActions,
  InlineEdit,
  QuickAddFragrance,
  useAdmin,
  type AdminSession,
} from './components/EditLink.js';
import type {
  AccordOption,
  Bootstrap,
  BrandFragranceCard as BrandCard,
  BrandLanding,
  Comparison,
  Country,
  Detail,
  Fragrance,
  Offer,
  Page,
} from './types.js';
import {
  cardActionLabel,
  concentrationSummary,
  fragranceCardDescription,
  longevityBands,
} from '../../../packages/shared/src/brand-landing.js';
import {
  cataloguePageExists,
  comparisonPath,
  siblingComparisonPath,
} from '../../../packages/shared/src/catalogue-seo.js';
import {
  affiliateDisclosure,
  clickTimeAffiliates,
  earnsCommission,
  type ClickTimeAffiliates,
} from '../../../packages/shared/src/affiliate-disclosure.js';
import {
  hasRecordedPrice,
  PRICE_MAX_AGE_MS,
  DELIVERY_MAX_AGE_MS,
  recentlyChecked,
} from '../../../packages/shared/src/buying.js';
import {
  fragranceIdentity,
  pageMetadata,
  type PageMetadata,
} from '../../../packages/shared/src/seo.js';
import {
  pageStructuredData,
  serializeStructuredData,
  type StructuredDataInput,
} from '../../../packages/shared/src/structured-data.js';
import { Admin } from './Admin.js';
import { Contact } from './Contact.js';
import { Privacy } from './Privacy.js';
import { AccountLink, AccountPage, AccountProvider } from './Account.js';
import { PageImageActions } from './components/PageImageActions.js';
import { SaveFragrance } from './components/SavedFragrances.js';
import { ContributePhotoButton, ImageSubmissionForm } from './components/ImageSubmissions.js';
import { LinkArrow } from './components/LinkArrow.js';
import { ScentIndex } from './components/ScentIndex.js';
import { ScentFinder } from './ScentFinder.js';
const Context = createContext<Bootstrap>({ url: '/', brands: [], countries: [] });
function StructuredData(
  props: Pick<StructuredDataInput, 'brand' | 'brandSummary' | 'detail' | 'items' | 'error'>,
) {
  const initial = useContext(Context),
    location = useLocation();
  const data = pageStructuredData({
    origin: initial.origin,
    href: location.pathname + location.search,
    ...props,
  });
  return data ? (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serializeStructuredData(data) }}
    />
  ) : null;
}
const fragranceOptionLabel = (x: Record<string, any>) =>
  (x.brand?.name ? x.brand.name + ' · ' : '') +
  x.name +
  (x.concentration ? ' ' + x.concentration : '');
const CountryContext = createContext<{
  country: string;
  setCountry: (v: string) => void;
  countries: Country[];
}>({ country: '', setCountry: () => {}, countries: [] });
function setMetadata(meta: PageMetadata) {
  document.title = meta.title;
  let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (meta.noindex) {
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.append(robots);
    }
    robots.content = meta.canonical.startsWith('/admin') ? 'noindex,nofollow' : 'noindex,follow';
  } else robots?.remove();
  for (const [selector, value] of [
    ['meta[name="description"]', meta.description],
    ['meta[property="og:title"]', meta.title],
    ['meta[property="og:description"]', meta.description],
    ['meta[name="twitter:title"]', meta.title],
    ['meta[name="twitter:description"]', meta.description],
  ])
    document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', value);
  const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canonical) {
    canonical.href = new URL(meta.canonical, new URL(canonical.href).origin).href;
    document
      .querySelector<HTMLMetaElement>('meta[property="og:url"]')
      ?.setAttribute('content', canonical.href);
  }
}
export function App({ initial }: { initial: Bootstrap }) {
  const location = useLocation();
  useEffect(() => {
    // Fragrance and brand landing pages set their own metadata once their data is available.
    if (location.pathname.startsWith('/fragrances/') || /^\/brands\/[^/]+$/.test(location.pathname))
      return;
    const brand = initial.brands.find((b) => '/brands/' + b.slug === location.pathname);
    setMetadata(
      pageMetadata(location.pathname + location.search, {
        brand,
        error:
          location.pathname.startsWith('/brands/') && !brand
            ? 'Brand not found'
            : initial.url === location.pathname + location.search
              ? initial.error
              : undefined,
      }),
    );
  }, [location.pathname, location.search, initial.brands]);
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    let anchor = location.hash.slice(1);
    try {
      anchor = decodeURIComponent(anchor);
    } catch {
      /* Invalid escapes can still be literal IDs. */
    }
    const target = document.getElementById(anchor);
    if (!target) return;
    const answer = target.closest('details');
    if (answer) answer.open = true;
    // Scroll to the question as well as its answer so the destination keeps its context.
    (answer ?? target).scrollIntoView({ block: 'start' });
  }, [location.pathname, location.hash]);
  const [country, setCountryState] = useState(() => defaultCountry(initial.countries));
  useEffect(() => {
    let saved: string | null = null,
      timeZone: string | undefined;
    try {
      saved = localStorage.getItem('identifrag-country');
    } catch {
      /* Browser detection still works without storage. */
    }
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      /* Fall back to browser language. */
    }
    setCountryState(
      defaultCountry(initial.countries, {
        saved,
        timeZone,
        languages: navigator.languages?.length ? navigator.languages : [navigator.language],
      }),
    );
  }, []);
  const setCountry = (value: string) => {
    if (!initial.countries.some((c) => c.code === value)) return;
    setCountryState(value);
    try {
      localStorage.setItem('identifrag-country', value);
    } catch {}
  };
  // Resolved after mount so the shared server-rendered markup stays identical for every visitor.
  const [session, setSession] = useState<AdminSession>({ admin: null, csrfToken: '' }),
    onAdminRoute = location.pathname.startsWith('/admin');
  useEffect(() => {
    if (onAdminRoute) return;
    let alive = true;
    api<AdminSession>('/api/v1/admin/whoami')
      .then((r) => {
        if (alive) setSession(r);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [onAdminRoute]);
  return (
    <Context.Provider value={initial}>
      <CountryContext.Provider value={{ country, setCountry, countries: initial.countries }}>
        <AdminContext.Provider value={session}>
          <AccountProvider>
            <a className="skip-link" href="#main">
              Skip to content
            </a>
            {(['/about', '/contact', '/privacy', '/guides'].includes(location.pathname) || location.pathname.startsWith('/guides/')) && <StructuredData />}
            <Routes>
              <Route path="/admin/*" element={<Admin />} />
              <Route
                path="*"
                element={
                  <>
                    <Header />
                    <main id="main" tabIndex={-1}>
                      <Routes>
                        <Route path="/" element={<Finder />} />
                        <Route path="/brands" element={<Brands />} />
                        <Route path="/notes" element={<Navigate to="/finder" replace />} />
                        <Route path="/finder" element={<ScentFinder initialResults={initial.scentFinder} initialQuery={new URL(initial.url, 'https://identifrag.example').search} />} />
                        <Route path="/compare" element={<CompareChooser />} />
                        <Route path="/families" element={<Navigate to="/finder" replace />} />
                        <Route path="/notes/:slug" element={<DiscoveryFinderRedirect kind="notes" />} />
                        <Route path="/families/:slug" element={<DiscoveryFinderRedirect kind="families" />} />
                        <Route path="/brands/:slug" element={<BrandPage />} />
                        <Route path="/fragrances/:slug" element={<FragrancePage />} />
                        <Route
                          path="/compare/:originalSlug/:alternativeSlug"
                          element={<ComparisonPage />}
                        />
                        <Route path="/about" element={<About />} />
                        <Route path="/guides" element={<Guides />} />
                        {(Object.keys(educationGuides) as EducationGuideSlug[]).map((slug) => <Route key={slug} path={guidePath(slug)} element={<EducationGuide slug={slug} oudFragrances={initial.oudFragrances} />} />)}
                        <Route path={guidePath('our-picks-autumn-2026')} element={<AutumnGuide />} />
                        <Route path={guidePath('testing-scents-101')} element={<TestingScentsGuide initialCountry={country} countries={initial.decantCountries ?? initial.countries} />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/profiles/:username" element={<PublicProfile />} />
                        {[
                          '/sign-in',
                          '/register',
                          '/account',
                          '/profile',
                          '/forgot-password',
                          '/reset-password',
                          '/verify-email',
                        ].map((path) => (
                          <Route key={path} path={path} element={<AccountPage />} />
                        ))}
                        <Route
                          path="*"
                          element={
                            <Notice
                              title="Page not found"
                              text="Try searching the fragrance collection."
                            />
                          }
                        />
                      </Routes>
                    </main>
                    <Footer />
                    <AdminBar />
                  </>
                }
              />
            </Routes>
          </AccountProvider>
        </AdminContext.Provider>
      </CountryContext.Provider>
    </Context.Provider>
  );
}
export function CountrySelect() {
  const { country, setCountry, countries } = useContext(CountryContext);
  return <CountryCombobox className="country-combobox--header" label="Buying country" country={country} countries={countries} onChange={setCountry} />;
}
// The homepage also pins a second copy of this header, carrying the search, once its own search has scrolled away.
export function Header({ search }: { search?: ReactNode } = {}) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const header = useRef<HTMLElement>(null);
  const navigationId = useId();
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1201px)');
    const close = () => setMenuOpen(false);
    desktop.addEventListener('change', close);
    return () => desktop.removeEventListener('change', close);
  }, []);
  useEffect(() => {
    if (!menuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [menuOpen]);
  return (
    <header
      className="site-header"
      ref={header}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && menuOpen) {
          event.preventDefault();
          setMenuOpen(false);
          menuButton.current?.focus();
        }
      }}
    >
      <Link to="/" className="wordmark" onClick={() => setMenuOpen(false)}>
        <BrandLogo />
      </Link>
      {search}
      <button
        className="menu-toggle"
        type="button"
        ref={menuButton}
        aria-expanded={menuOpen}
        aria-controls={navigationId}
        aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        onClick={() => setMenuOpen((open) => !open)}
      >
        {menuOpen ? <X size={22} aria-hidden /> : <Menu size={22} aria-hidden />}
        <span>{menuOpen ? 'Close' : 'Menu'}</span>
      </button>
      <div id={navigationId} className={'header-navigation' + (menuOpen ? ' is-open' : '')}>
        <nav
          aria-label="Main navigation"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('a')) setMenuOpen(false);
          }}
        >
          <Link to="/" aria-current={location.pathname === '/' ? 'page' : undefined}>
            Explore
          </Link>
          <Link
            to="/brands"
            aria-current={
              location.pathname === '/brands' || location.pathname.startsWith('/brands/')
                ? 'page'
                : undefined
            }
          >
            Brands
          </Link>
          <Link to="/finder" aria-current={location.pathname === '/finder' ? 'page' : undefined}>Find by scent</Link>
          <Link to="/compare" aria-current={location.pathname.startsWith('/compare') ? 'page' : undefined}>Compare</Link>
          <Link to="/about" aria-current={location.pathname === '/about' ? 'page' : undefined}>
            About
          </Link>
          <Link to="/contact" aria-current={location.pathname === '/contact' ? 'page' : undefined}>
            Contact
          </Link>
          <AccountLink />
        </nav>
        <CountrySelect />
      </div>
    </header>
  );
}
// Visitors see no editor entrance; signed-in editors get their workspace links.
export function Footer() {
  const { admin } = useAdmin();
  return (
    <footer className="site-footer">
      <div className="footer-legal">
        <span>© {new Date().getFullYear()} Identifrag</span>
        <Link to="/about">About</Link>
        <Link to="/contact">Contact</Link>
        <Link to="/privacy">Privacy &amp; Cookies</Link>
        <Link to="/guides">Guides</Link>
        <Link to="/finder">Find by scent</Link>
        <Link to="/compare">Compare</Link>
        <p className="footer-notice">
          Product images on Identifrag are artificially generated, not official brand photography.
          All trade marks belong to their owners. Identifrag is independent and not affiliated with
          any brand. Check the retailer for the exact product.
        </p>
        {admin && (
          <nav aria-label="Editorial workspace" className="footer-admin">
            <Link to="/admin/fragrances">Editorial workspace</Link>
            <Link to="/admin/fragrances/new">Add a fragrance</Link>
            <span className="footer-admin-who">Signed in as {admin.email}</span>
          </nav>
        )}
      </div>
    </footer>
  );
}
export function Notice({ title, text }: { title: string; text: string }) {
  return (
    <section className="notice">
      <h1>{title}</h1>
      <p>{text}</p>
      <Link to="/" className="text-link">
        Explore fragrances
      </Link>
    </section>
  );
}
// A fragrance page is usually reached from the results, so the way back remembers the search that was left behind.
const exploreStore = 'identifrag-explore';
let exploreSearch = '';
function rememberExplore(search: string) {
  exploreSearch = search;
  try {
    sessionStorage.setItem(exploreStore, search);
  } catch {
    /* The address bar still carries the search when storage is disabled. */
  }
}
function useExploreHref() {
  const [search, setSearch] = useState(exploreSearch);
  // Stored searches are read after mount so the server-rendered markup and the first client render still agree.
  useEffect(() => {
    if (exploreSearch) {
      setSearch(exploreSearch);
      return;
    }
    try {
      const saved = sessionStorage.getItem(exploreStore) ?? '';
      if (saved) {
        exploreSearch = saved;
        setSearch(saved);
      }
    } catch {}
  }, []);
  return '/' + search;
}
// One shared path keeps catalogue, brand and comparison navigation in the same place.
function CatalogueTrail({
  brand,
  fragrance,
  comparison = false,
  directory = false,
}: {
  brand?: Fragrance['brand'];
  fragrance?: Fragrance;
  comparison?: boolean;
  directory?: boolean;
}) {
  const { brands } = useContext(Context),
    exploreHref = useExploreHref();
  const knownBrand = brand && brands.some((item) => item.slug === brand.slug);
  const steps: { label: string; href?: string }[] = [{ label: 'Explore', href: exploreHref }];
  if (directory || brand) steps.push({ label: 'Brands', href: directory ? undefined : '/brands' });
  if (brand)
    steps.push({
      label: brand.name,
      href: fragrance && knownBrand ? '/brands/' + encodeURIComponent(brand.slug) : undefined,
    });
  if (fragrance)
    steps.push({
      label: fragrance.name,
      href: comparison ? '/fragrances/' + encodeURIComponent(fragrance.slug) : undefined,
    });
  if (comparison) steps.push({ label: 'Comparison' });
  return (
    <nav className="catalogue-trail" aria-label="Breadcrumb">
      <ol>
        {steps.map((step, index) => (
          <li key={index}>
            {step.href ? (
              <Link
                to={step.href}
                aria-label={
                  comparison && index === steps.length - 2
                    ? 'All fragrance comparisons for ' + step.label
                    : undefined
                }
              >
                {step.label}
              </Link>
            ) : (
              <span aria-current={index === steps.length - 1 ? 'page' : undefined}>
                {step.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
// The results stay on screen while the next page loads, so a page change fades instead of blanking.
function usePagedCatalogue<T = Page>(url: string, initial?: { url: string; data: T }) {
  const { data, loading, error, reload } = useResource<T>(url, initial);
  const previous = useRef<T | null>(null);
  if (data) previous.current = data;
  return {
    shown: data ?? previous.current,
    loading,
    error,
    reload,
    stale: !data && !!previous.current,
  };
}
// A short window keeps the control the same width whether the collection runs to five pages or fifty.
function pageWindow(page: number, count: number): (number | 'gap')[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const start = Math.min(Math.max(page - 3, 1), count - 6),
    slots: (number | 'gap')[] = Array.from({ length: 7 }, (_, i) => start + i);
  if (start > 1) {
    slots[0] = 1;
    slots[1] = 'gap';
  }
  if (start + 6 < count) {
    slots[5] = 'gap';
    slots[6] = count;
  }
  return slots;
}
function Pagination({
  page,
  pageCount,
  total,
  busy,
  onNavigate,
}: {
  page: number;
  pageCount: number;
  total: number;
  busy: boolean;
  onNavigate: () => void;
}) {
  const [params] = useSearchParams(),
    location = useLocation();
  const href = (page: number) => {
    const next = new URLSearchParams(params);
    next.delete('type');
    page > 1 ? next.set('page', String(page)) : next.delete('page');
    return location.pathname + (next.size ? '?' + next.toString() : '');
  };
  const onClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) onNavigate();
  };
  return (
    <div className="pagination-block">
      <nav className="page-nav" aria-label="Pagination">
        {page > 1 ? (
          <Link className="page-arrow" to={href(page - 1)} onClick={onClick}>
            <LinkArrow direction="left" size={15} />
            <span>Previous</span>
          </Link>
        ) : (
          <span className="page-arrow" aria-disabled="true">
            <LinkArrow direction="left" size={15} />
            <span>Previous</span>
          </span>
        )}
        <ol className="page-numbers">
          {pageWindow(page, pageCount).map((entry, i) =>
            entry === 'gap' ? (
              <li className="page-gap" key={'gap-' + i} aria-hidden>
                …
              </li>
            ) : (
              <li key={entry}>
                <Link
                  className={'page-number' + (entry === page ? ' is-current' : '')}
                  to={href(entry)}
                  aria-label={'Page ' + entry + ' of ' + pageCount}
                  aria-current={entry === page ? 'page' : undefined}
                  onClick={onClick}
                >
                  {entry}
                </Link>
              </li>
            ),
          )}
        </ol>
        {page < pageCount ? (
          <Link className="page-arrow" to={href(page + 1)} onClick={onClick}>
            <span>Next</span>
            <LinkArrow size={15} />
          </Link>
        ) : (
          <span className="page-arrow" aria-disabled="true">
            <span>Next</span>
            <LinkArrow size={15} />
          </span>
        )}
      </nav>
      <p className="page-status" role="status">
        {busy
          ? 'Loading page ' + page + '…'
          : 'Page ' + page + ' of ' + pageCount + ' · ' + total + ' fragrances'}
      </p>
    </div>
  );
}
function Finder() {
  const initial = useContext(Context),
    [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  useEffect(() => {
    if (!params.has('type')) return;
    const next = new URLSearchParams(params);
    next.delete('type');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const canonical = new URLSearchParams(params);
  canonical.delete('country');
  canonical.delete('concentration');
  canonical.delete('type');
  const url = '/api/v1/fragrances' + (canonical.size ? '?' + canonical.toString() : '');
  const initialQuery = new URL(initial.url, 'http://localhost').search;
  const { shown, loading, error, reload, stale } = usePagedCatalogue(
    url,
    initial.page ? { url: '/api/v1/fragrances' + initialQuery, data: initial.page } : undefined,
  );
  useEffect(() => setQuery(params.get('q') ?? ''), [params.get('q')]);
  useEffect(() => rememberExplore(params.size ? '?' + params.toString() : ''), [params]);
  useEffect(() => {
    if (query === (params.get('q') ?? '')) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      query.trim() ? next.set('q', query.trim()) : next.delete('q');
      next.delete('page');
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, params, setParams]);
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete('page');
    next.delete('concentration');
    setParams(next);
  };
  const hasFilters = ['brand', 'gender', 'accord'].some((key) => Boolean(params.get(key)));
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    ['brand', 'gender', 'accord', 'page', 'concentration', 'type'].forEach((key) =>
      next.delete(key),
    );
    setParams(next);
  };
  // Accords arrive with a server-rendered finder; arriving from another page fetches them instead.
  const accordOptions =
    useResource<AccordOption[]>(
      '/api/v1/accords',
      initial.accords ? { url: '/api/v1/accords', data: initial.accords } : undefined,
    ).data ?? [];
  const searchRef = useRef<HTMLFormElement | null>(null);
  // Further pages load beneath the first as the reader nears the foot of the grid. They belong to
  // one query, so a new search or filter starts the list again from its first page.
  const [more, setMore] = useState<{ url: string; pages: Page[]; loading: boolean; error: string }>({
    url,
    pages: [],
    loading: false,
    error: '',
  });
  const extra = more.url === url ? more.pages : [];
  const last = extra.at(-1) ?? shown;
  const nextPage =
    last && !stale && !error && last.page * last.pageSize < last.total ? last.page + 1 : null;
  const loadingMore = more.url === url && more.loading;
  const moreError = more.url === url ? more.error : '';
  const loadMore = () => {
    if (!nextPage || loadingMore) return;
    const target = url,
      next = new URL(url, 'http://localhost');
    next.searchParams.set('page', String(nextPage));
    setMore((current) => ({
      url: target,
      pages: current.url === target ? current.pages : [],
      loading: true,
      error: '',
    }));
    api<Page>(next.pathname + next.search)
      .then((page) =>
        setMore((current) =>
          current.url === target
            ? { ...current, pages: [...current.pages, page], loading: false }
            : current,
        ),
      )
      .catch((e: Error) =>
        setMore((current) =>
          current.url === target ? { ...current, loading: false, error: e.message } : current,
        ),
      );
  };
  const sentinel = useRef<HTMLAnchorElement | null>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  // Observing afresh after each page means a sentinel still in view once a short page lands asks again.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !nextPage || loadingMore || moreError || typeof IntersectionObserver === 'undefined')
      return;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && loadMoreRef.current(),
      { rootMargin: '0px 0px 900px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextPage, loadingMore, moreError, url]);
  const items = shown
    ? [...shown.items, ...extra.flatMap((page) => page.items)].filter(
        (item, index, all) => all.findIndex((other) => other.id === item.id) === index,
      )
    : [];
  const pageHref = (page: number) => {
    const next = new URLSearchParams(params);
    next.delete('type');
    page > 1 ? next.set('page', String(page)) : next.delete('page');
    return '/' + (next.size ? '?' + next.toString() : '');
  };
  // A reader who arrives part-way through, from a search result or an old link, can step back.
  const previousPage = shown && shown.page > 1 ? shown.page - 1 : null;
  // Once the search field has scrolled away, a slim bar brings it back at the top of the window.
  const [searchAway, setSearchAway] = useState(false);
  useEffect(() => {
    const el = searchRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) =>
      setSearchAway(!entry.isIntersecting && entry.boundingClientRect.top < 0),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // While someone types in the bar it stays put, even if fewer results bring the main field back into view.
  const [barFocused, setBarFocused] = useState(false);
  const barShown = searchAway || barFocused;
  const collectionRef = useRef<HTMLElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  // A search typed in the bar brings its results up beneath the bar rather than leaving the reader mid-list.
  useEffect(() => {
    if (!barFocused || !collectionRef.current) return;
    const top =
      collectionRef.current.getBoundingClientRect().top +
      window.scrollY -
      (barRef.current?.offsetHeight ?? 0) -
      12;
    if (Math.abs(window.scrollY - top) > 4) window.scrollTo({ top });
  }, [params.get('q')]);
  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    update('q', query.trim());
  };
  const clearSearch = () => {
    setQuery('');
    update('q', '');
  };
  const missingPage =
    Boolean(shown && !cataloguePageExists(shown)) || error === 'Catalogue page not found';
  useEffect(() => {
    if (missingPage)
      setMetadata(
        pageMetadata('/' + (params.size ? '?' + params.toString() : ''), {
          error: 'Catalogue page not found',
        }),
      );
  }, [missingPage, params]);
  if (missingPage)
    return (
      <Notice
        title="Catalogue page not found"
        text="This page is beyond the end of the collection. Return to the fragrance collection to browse available pages."
      />
    );
  const showingHome = !['q', 'brand', 'gender', 'accord', 'page'].some((key) => params.has(key));
  return (
    <div className="finder">
      <div
        ref={barRef}
        className={'finder-stickybar' + (barShown ? ' is-visible' : '')}
        inert={!barShown}
      >
        <Header
          search={
            <form
              className="search-field"
              role="search"
              aria-label="Search the collection"
              onSubmit={submitSearch}
              onFocus={() => setBarFocused(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setBarFocused(false);
              }}
            >
              <Search aria-hidden size={19} />
              <label className="sr-only" htmlFor="fragrance-search-sticky">
                Search fragrances, brands or gender
              </label>
              <input
                id="fragrance-search-sticky"
                autoComplete="off"
                type="search"
                placeholder="Search fragrances"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" aria-label="Clear search" onClick={clearSearch}>
                  <X size={16} />
                </button>
              )}
            </form>
          }
        />
      </div>
      {!stale && !error && (
        <StructuredData
          items={shown?.items.map((f) => ({
            name: fragranceIdentity(f),
            path: '/fragrances/' + encodeURIComponent(f.slug),
          }))}
        />
      )}
      <section className="finder-intro">
        <h1>
          <span>Find your next fragrance.</span> <span>Follow the notes and accords you love.</span>
        </h1>
        <form className="search-field" role="search" ref={searchRef} onSubmit={submitSearch}>
          <Search aria-hidden size={25} />
          <label className="sr-only" htmlFor="fragrance-search">
            Search fragrances, brands or gender
          </label>
          <input
            id="fragrance-search"
            autoComplete="off"
            type="search"
            placeholder="Search fragrances, brands or gender"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={clearSearch}>
              <X size={18} />
            </button>
          )}
        </form>
        <FinderFilters
          brands={initial.brands}
          accords={accordOptions}
          params={params}
          update={update}
          clear={clearFilters}
        />
      </section>
      <section className="collection" aria-label="Fragrance collection" ref={collectionRef}>
        {showingHome && <PopularComparisonCoverflow initialCards={initial.popularComparisonCards} />}
        <div className="finder-results-bar">
          <span className="result-count" role="status">
            {loading ? 'Searching…' : shown ? shown.total + ' fragrances' : ''}
          </span>
          <FilterSelect
            label="Sort by"
            value={
              ['az', 'popular', 'recent', 'relevance'].includes(params.get('sort') ?? '')
                ? params.get('sort')!
                : defaultSearchSort(params.get('q') ?? '')
            }
            options={[
              { value: 'relevance', label: 'Relevance' },
              { value: 'az', label: 'A–Z' },
              { value: 'popular', label: 'Most Popular' },
              { value: 'recent', label: 'Recently Added' },
            ]}
            onChange={(value) => update('sort', value)}
          />
        </div>
        {previousPage && !error && (
          <div className="finder-earlier">
            <Link className="text-link" to={pageHref(previousPage)}>
 Show earlier fragrances
            </Link>
          </div>
        )}
        {error ? (
          <div className="error-message" role="alert">
            {error} <button onClick={() => window.location.reload()}>Try again</button>
          </div>
        ) : items.length ? (
          <div className={'product-grid' + (stale ? ' is-loading' : '')} aria-busy={stale}>
            {items.map((f) => (
              <ProductCard key={f.id} fragrance={f} onSaved={reload} />
            ))}
          </div>
        ) : !loading ? (
          <section className="empty-state">
            <Search size={28} />
            <h2>{params.size ? 'No fragrances found' : 'The collection is being prepared'}</h2>
            <p>
              {params.size
                ? 'Try a shorter name, another spelling, or clear your filters.'
                : 'Verified fragrances will appear here as the editorial catalogue is published.'}
            </p>
            {hasFilters ? (
              <button className="button secondary" onClick={clearFilters}>
                {query ? 'Clear filters, keep search' : 'Clear filters'}
              </button>
            ) : query ? (
              <button
                className="button secondary"
                onClick={() => {
                  setQuery('');
                  update('q', '');
                }}
              >
                Clear search
              </button>
            ) : null}
          </section>
        ) : (
          <p className="loading-state" role="status">
            Loading the collection…
          </p>
        )}
        {/* The next page is a real link, so crawlers and readers without scripts can still reach it;
            with scripts it loads in place as it nears the viewport. */}
        {nextPage && (
          <div className="finder-more">
            {moreError ? (
              <p className="error-message" role="alert">
                {moreError}{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMore((current) => ({ ...current, error: '' }));
                  }}
                >
                  Try again
                </button>
              </p>
            ) : (
              <Link
                ref={sentinel}
                className="button secondary"
                to={pageHref(nextPage)}
                aria-disabled={loadingMore}
                onClick={(e) => {
                  e.preventDefault();
                  loadMore();
                }}
              >
                {loadingMore ? 'Loading more fragrances…' : 'Show more fragrances'}
              </Link>
            )}
          </div>
        )}
        {/* Sits after the grid, just above the footer. On the homepage it is reached once the collection has
            fully loaded; on searches and filters it appears once every result is on the page. */}
        {(showingHome || (shown && !stale && !error && !nextPage)) && <ScentIndex initialCounts={initial.discoveryCounts} />}
      </section>
    </div>
  );
}
export function Bottle({
  fragrance: f,
  thumb = false,
  children,
}: {
  fragrance: Fragrance;
  thumb?: boolean;
  children?: ReactNode;
}) {
  const media = f.media;
  if (!media)
    return (
      <div className="image-missing">
        <span>Image coming soon</span>
        {children}
      </div>
    );
  return (
    <>
      <img
        className="bottle-image"
        src={'/media/' + encodeURIComponent(media.id) + '?' + new URLSearchParams({ ...(thumb ? { size: 'thumb' } : {}), ...(media.updatedAt ? { v: String(Date.parse(media.updatedAt)) } : {}) })}
        alt={media.alt}
        width={media.width}
        height={media.height}
        loading={thumb ? 'lazy' : 'eager'}
        decoding="async"
      />
    </>
  );
}
// Community sentiment at a glance; the card is a link, so the tally is read as part of its name.
function ReviewTally({ reviews }: { reviews?: { positive: number; negative: number } }) {
  if (!reviews || !reviewsVisible) return null;
  const { positive, negative } = reviews;
  return (
    <span className={'review-tally' + (positive + negative ? '' : ' review-tally--empty')}>
      <span className="review-tally-item review-tally-item--positive">
        <ThumbsUp size={13} aria-hidden="true" />
        {positive}
        <span className="sr-only"> positive {positive === 1 ? 'review' : 'reviews'},</span>
      </span>
      <span className="review-tally-item review-tally-item--negative">
        <ThumbsDown size={13} aria-hidden="true" />
        {negative}
        <span className="sr-only"> negative {negative === 1 ? 'review' : 'reviews'}</span>
      </span>
    </span>
  );
}
function ProductCard({
  fragrance: f,
  onSaved,
  sharedAccords,
}: {
  fragrance: Fragrance;
  onSaved: () => void;
  sharedAccords?: string[];
}) {
  const { admin } = useAdmin();
  const gender =
    f.gender === 'male'
      ? { label: 'Male', Icon: Mars }
      : f.gender === 'female'
        ? { label: 'Female', Icon: Venus }
        : f.gender === 'unisex'
          ? { label: 'Unisex', Icon: VenusAndMars }
          : null;
  const card = (
    <Link
      className="product-card"
      to={'/fragrances/' + f.slug}
    >
      <div className="product-photo">
        {gender && (
          <span className={'gender-tag gender-tag--' + f.gender}>
            <gender.Icon size={14} aria-hidden="true" />
            {gender.label}
          </span>
        )}
        <Bottle fragrance={f} thumb />
        <ReviewTally reviews={f.reviews} />
      </div>
      <div className="product-copy">
        <span className="product-brand">{f.brand?.name}</span>
        <h2>{f.name}</h2>
        <p>{f.concentration || 'Concentration under review'}</p>
        {sharedAccords?.length ? (
          <div className="similar-card-accords">
            <p>Shared accords</p>
            <ul className="overview-keywords">
              {sharedAccords.slice(0, 3).map((accord) => (
                <li key={accord}>{accord}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <span className="product-action">
          View fragrance
        </span>
      </div>
    </Link>
  );
  // The card is itself a link, so the editorial control sits beside it rather than nested inside.
  return (
    <div className="fragrance-save-card">
      {card}
      <SaveFragrance fragrance={f} compact />
      {admin && (
        <InlineEdit
          entity="fragrances"
          id={f.id}
          label="Edit"
          srLabel={'Edit ' + f.name}
          onSaved={onSaved}
          fields={[
            { key: 'name', label: 'Fragrance name' },
            { key: 'concentration', label: 'Concentration' },
          ]}
        />
      )}
    </div>
  );
}
function RecommendationBadge({ reason }: { reason: string }) {
  return (
    <details className="recommendation-disclosure">
      <summary className="recommendation">
        <Check size={13} aria-hidden="true" />
        <span>Recommended</span>
        <Info className="recommendation-info" size={12} aria-hidden="true" />
      </summary>
      <div className="recommendation-reason">
        <strong>Why we recommend it</strong>
        <p>
          {reason.trim() ||
            'Our editors selected this fragrance as a standout in this comparison.'}
        </p>
      </div>
    </details>
  );
}
type FinderResult = { slug: string; name: string; brand: { name: string } | null; concentration: string; keyNotes: string[]; accords: { name: string; intensity: number }[]; longevity: string | null; price: number | null; score: number };
function CompareSearch({ label, selected, onSelect }: { label: string; selected: Fragrance | null; onSelect: (value: Fragrance) => void }) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Fragrance[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!query.trim()) return;
    setBusy(true); setError('');
    try { setMatches((await api<Page>('/api/v1/fragrances?q=' + encodeURIComponent(query.trim()))).items); }
    catch { setError('Search is unavailable. Try again.'); }
    finally { setBusy(false); }
  };
  return <div className="compare-choice"><h2>{label}</h2>
    {selected && <p className="compare-selected">Selected: {fragranceIdentity(selected)}</p>}
    <form onSubmit={search}><label className="sr-only" htmlFor={'compare-' + label}>{label} fragrance name</label><input id={'compare-' + label} type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search fragrance name" /><button type="submit" disabled={busy}>{busy ? 'Searching…' : 'Search'}</button></form>
    {error && <p role="alert">{error}</p>}
    {matches.length > 0 && <ul>{matches.map(item => <li key={item.id}><button type="button" onClick={() => { onSelect(item); setMatches([]); }}>{fragranceIdentity(item)}</button></li>)}</ul>}
    {matches.length === 0 && query && !selected && !busy && <p>Search by a brand or fragrance name to choose a release.</p>}
  </div>;
}
function CompareChooser() {
  const [first, setFirst] = useState<Fragrance | null>(null);
  const [second, setSecond] = useState<Fragrance | null>(null);
  return <div className="discovery-page compare-chooser"><h1>Compare two fragrances</h1>
    <p>Choose the exact releases you want to inspect side by side. The comparison shows recorded notes, accords and wear estimates.</p>
    <div className="compare-choice-grid"><CompareSearch label="First" selected={first} onSelect={setFirst} /><CompareSearch label="Second" selected={second} onSelect={setSecond} /></div>
    {first && second && (first.slug === second.slug ? <p>Choose two different fragrances.</p> : <Link className="button" to={comparisonPath(first.slug, second.slug)}>Compare selected fragrances</Link>)}
  </div>;
}
function PerfumeFinder() {
  const [like, setLike] = useState('vanilla');
  const [avoid, setAvoid] = useState('');
  const [season, setSeason] = useState('any');
  const [budget, setBudget] = useState('0');
  const [strength, setStrength] = useState('any');
  const [results, setResults] = useState<FinderResult[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError('');
    const params = new URLSearchParams({ like, avoid, season, budget, strength });
    try { setResults(await api<FinderResult[]>('/api/v1/finder?' + params)); }
    catch { setError('The finder could not load. Try again.'); }
    finally { setBusy(false); }
  };
  return <div className="discovery-page perfume-finder">
    <h1>Find a fragrance by scent</h1>
    <p>Tell us what you enjoy. Results are ranked from recorded notes and accords; price and season are preferences, not guarantees.</p>
    <form className="finder-quiz" onSubmit={submit}>
      <label>1. A note you like <select value={like} onChange={event => setLike(event.target.value)}>{noteHubs.map(note => <option key={note} value={note}>{note}</option>)}</select></label>
      <label>2. A note to avoid <select value={avoid} onChange={event => setAvoid(event.target.value)}><option value="">None</option>{noteHubs.filter(note => note !== like).map(note => <option key={note} value={note}>{note}</option>)}</select></label>
      <label>3. When you might wear it <select value={season} onChange={event => setSeason(event.target.value)}>{['any','spring','summer','autumn','winter'].map(value => <option key={value} value={value}>{value === 'any' ? 'Any season' : value}</option>)}</select></label>
      <label>4. UK price preference <select value={budget} onChange={event => setBudget(event.target.value)}><option value="0">Any price</option><option value="50">Around £50 or less</option><option value="100">Around £100 or less</option><option value="200">Around £200 or less</option></select></label>
      <label>5. Projection preference <select value={strength} onChange={event => setStrength(event.target.value)}><option value="any">Any strength</option><option value="soft">Soft</option><option value="moderate">Moderate</option><option value="strong">Strong</option></select></label>
      <button className="button" type="submit" disabled={busy}>{busy ? 'Finding fragrances…' : 'Find fragrances'}</button>
    </form>
    {error && <p role="alert">{error}</p>}
    {results && <section aria-live="polite" className="finder-quiz-results"><h2>{results.length ? 'Your scent matches' : 'No matches yet'}</h2>
      <p>Based on recorded notes and accords. UK prices are shown only when a non-Amazon offer is recorded; check retailers for current prices.</p>
      <div className="discovery-list">{results.map(item => <article key={item.slug}>
        <div><h3><Link to={'/fragrances/' + encodeURIComponent(item.slug)}>{fragranceIdentity(item)}</Link></h3><p>{item.accords.map(accord => accord.name).join(' · ') || item.keyNotes.join(' · ')}</p></div>
        <div className="discovery-fact">{item.longevity ? item.longevity + ' wear estimate' : 'Wear estimate pending'}</div>
        <div className="discovery-fact">{item.price === null ? 'Price not recorded' : `From £${item.price.toFixed(2)} recorded`}</div>
      </article>)}</div>
      {!results.length && <p>Try another note or remove a note to avoid.</p>}
    </section>}
  </div>;
}

function DiscoveryFinderRedirect({ kind }: { kind: 'notes' | 'families' }) {
  const { slug = '' } = useParams();
  const [current] = useSearchParams();
  const next = new URLSearchParams();
  next.set(kind, slug);
  for (const key of ['gender', 'concentration', 'season', 'budget']) {
    const value = current.get(key);
    if (value) next.set(key, value);
  }
  return <Navigate to={'/finder?' + next} replace />;
}

function DiscoveryDirectory({ kind }: { kind: 'notes' | 'families' }) {
  const slugs = kind === 'notes' ? [...noteHubs] : Object.keys(familyHubs);
  return <div className="discovery-page">
    <h1>{kind === 'notes' ? 'Explore perfume notes' : 'Explore scent families'}</h1>
    <p>Find fragrances by the notes and accords recorded in their scent profiles.</p>
    <div className="discovery-links">{slugs.map(slug =>
      <Link key={slug} to={discoveryPath(kind, slug)}>{slug}</Link>)}</div>
    <p><Link className="text-link" to="/">Search the full fragrance collection</Link></p>
  </div>;
}

function DiscoveryPage({ kind }: { kind: 'notes' | 'families' }) {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const { discovery: initialHub, url } = useContext(Context);
  const path = discoveryPath(kind, slug);
  const initialUrl = new URL(url, 'https://identifrag.example');
  const initial = initialUrl.pathname === path && initialUrl.search === location.search ? initialHub : undefined;
  const resourceUrl = '/api/v1/discovery/' + kind + '/' + encodeURIComponent(slug) + (params.size ? '?' + params : '');
  const resource = useResource<DiscoveryHub>(resourceUrl, initial ? { url: resourceUrl, data: initial } : undefined);
  const hub = resource.data ?? initial;
  const gender = params.get('gender') ?? '';
  const concentration = params.get('concentration') ?? '';
  const season = params.get('season') ?? '';
  const budget = params.get('budget') ?? '';
  const updateFilter = (key: string, value: string) => { const next = new URLSearchParams(params); value ? next.set(key, value) : next.delete(key); next.delete('page'); setParams(next); };
  if (!hub && resource.error) return <Notice title="Discovery page unavailable" text="Choose another note or scent family." />;
  const label = slug[0]?.toUpperCase() + slug.slice(1);
  const rows = hub?.items ?? [];
  const concentrations = hub?.concentrations ?? [...new Set((hub?.items ?? []).map(item => item.concentration).filter(Boolean))].sort();
  return <div className="discovery-page">
    <nav className="discovery-trail" aria-label="Breadcrumb"><Link to="/">Explore</Link><span>/</span><Link to={'/' + kind}>{kind === 'notes' ? 'Notes' : 'Families'}</Link><span>/</span><span>{label}</span></nav>
    <h1>{label} perfumes</h1>
    <p>{kind === 'notes'
      ? `Explore fragrances with ${slug} among their recorded key notes or accords.`
      : `Explore fragrances with accords and notes associated with the ${slug} family.`}</p>
    <p className="discovery-method">{hub?.total ?? 0} fragrances match. Ordered by recorded accord intensity, then key note prominence. These are editorial scent estimates, not measured concentrations. Season cues use recorded notes; products without a recorded UK price remain visible. Buying links do not affect the order.</p>
    <div className="discovery-filters">
      <label>Gender <select value={gender} onChange={event => updateFilter('gender', event.target.value)}><option value="">All</option><option value="female">Female</option><option value="male">Male</option><option value="unisex">Unisex</option></select></label>
      <label>Concentration <select value={concentration} onChange={event => updateFilter('concentration', event.target.value)}><option value="">All</option>{concentrations.map(value => <option key={value} value={value.toLowerCase()}>{value}</option>)}</select></label>
      <label>Season cue <select value={season} onChange={event => updateFilter('season', event.target.value)}><option value="">Any</option>{['spring','summer','autumn','winter'].map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <label>UK price preference <select value={budget} onChange={event => updateFilter('budget', event.target.value)}><option value="">Any</option><option value="50">£50 or less</option><option value="100">£100 or less</option><option value="200">£200 or less</option></select></label>
    </div>
    {!hub && <p role="status">Loading fragrances…</p>}
    {hub && <>
      <StructuredData items={hub.items.map(item => ({ name: fragranceIdentity(item), path: '/fragrances/' + encodeURIComponent(item.slug) }))} />
      <div className="discovery-list">{rows.map(item => <article key={item.slug}>
        <div><h2><Link to={'/fragrances/' + encodeURIComponent(item.slug)}>{fragranceIdentity(item)}</Link></h2><p>{item.accords.slice(0, 3).map(accord => accord.name).join(' · ') || item.keyNotes.slice(0, 3).join(' · ')}</p></div>
        <div className="discovery-fact">{item.keyNotes.find(note => note.toLowerCase().includes(slug)) ?? item.accords.find(accord => accord.name.toLowerCase().includes(slug))?.name ?? 'Recorded accord'}</div>
        <div className="discovery-fact">{item.longevity ? item.longevity + ' wear estimate' : 'Wear estimate pending'}{item.price != null ? ` · From £${item.price.toFixed(2)} recorded` : ''}</div>
      </article>)}</div>
      {!rows.length && <p>No fragrances match these filters.</p>}
      {hub.total > (hub.pageSize ?? 30) && <nav className="discovery-pagination" aria-label="Hub pages">
        {page > 1 && <Link to={path + (() => { const next = new URLSearchParams(params); page === 2 ? next.delete('page') : next.set('page', String(page - 1)); return next.size ? '?' + next : ''; })()}>Previous page</Link>}
        <span>Page {page} of {Math.ceil(hub.total / (hub.pageSize ?? 30))}</span>
        {page * (hub.pageSize ?? 30) < hub.total && <Link to={path + (() => { const next = new URLSearchParams(params); next.set('page', String(page + 1)); return '?' + next; })()}>Next page</Link>}
      </nav>}
    </>}
    <div className="discovery-related"><h2>Explore more scents</h2><div className="discovery-links">{(kind === 'notes' ? noteHubs : Object.keys(familyHubs)).filter(value => value !== slug).slice(0, 5).map(value => <Link key={value} to={discoveryPath(kind, value)}>{value}</Link>)}</div></div>
  </div>;
}

function Brands() {
  const { brands: initialBrands } = useContext(Context);
  const [query, setQuery] = useState('');
  const [brands, setBrands] = useState(initialBrands);
  const filtered = brands.filter((b) => b.name.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="brands-page">
      <StructuredData
        items={filtered.map((b) => ({
          name: b.name,
          path: '/brands/' + encodeURIComponent(b.slug),
        }))}
      />
      <CatalogueTrail directory />
      <h1>Browse fragrances by brand</h1>
      <p>Explore each house’s fragrances, notes and accords.</p>
      <label className="search-field">
        <Search size={20} />
        <span className="sr-only">Search brands</span>
        <input
          placeholder="Search brands"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <div className="brand-directory">
        {filtered.map((b) => (
          <div className="brand-row" key={b.id}>
            <Link className="brand-link" to={'/brands/' + encodeURIComponent(b.slug)}>
              {b.name}
            </Link>
            <InlineEdit
              entity="brands"
              id={b.id}
              label="Edit"
              srLabel={'Edit ' + b.name}
              fields={[{ key: 'name', label: 'Brand name' }]}
              onSaved={(row) =>
                setBrands((list) =>
                  list.map((x) => (x.id === b.id ? { ...x, name: row.name, slug: row.slug } : x)),
                )
              }
            />
          </div>
        ))}
      </div>
      {!filtered.length && <p>No published brands match your search.</p>}
    </div>
  );
}
// Brand cards describe only what the catalogue records: accord estimates, the longevity category and
// published comparison counts. Anything missing is left out rather than filled in.
function BrandFragranceCard({
  fragrance: f,
  onSaved,
}: {
  fragrance: BrandCard;
  onSaved: () => void;
}) {
  const { admin } = useAdmin();
  const gender =
    f.gender === 'male'
      ? { label: 'Male', Icon: Mars }
      : f.gender === 'female'
        ? { label: 'Female', Icon: Venus }
        : f.gender === 'unisex'
          ? { label: 'Unisex', Icon: VenusAndMars }
          : null;
  const description = fragranceCardDescription(f);
  const card = (
    <Link
      className="product-card brand-card"
      to={'/fragrances/' + encodeURIComponent(f.slug)}
    >
      <div className="product-photo">
        {gender && (
          <span className={'gender-tag gender-tag--' + f.gender}>
            <gender.Icon size={14} aria-hidden="true" />
            {gender.label}
          </span>
        )}
        <Bottle fragrance={f} thumb />
        <ReviewTally reviews={f.reviews} />
      </div>
      <div className="product-copy">
        <h3>{f.name}</h3>
        <p className="brand-card-meta">
          <span>{f.concentration || 'Concentration under review'}</span>
        </p>
        {f.accords.length > 0 && (
          <ul className="overview-keywords brand-card-accords" aria-label="Main accords">
            {f.accords.map((accord) => (
              <li key={accord.name}>{accord.name}</li>
            ))}
          </ul>
        )}
        {f.longevity && (
          <p className="brand-card-longevity">
            <span>Longevity</span> {f.longevity} · {longevityBands[f.longevity]}
          </p>
        )}
        {description && <p className="brand-card-summary">{description}</p>}
        <span className="product-action">
          {cardActionLabel(f)}
        </span>
      </div>
    </Link>
  );
  return (
    <div className="fragrance-save-card">
      {card}
      <SaveFragrance fragrance={f} compact />
      {admin && (
        <InlineEdit
          entity="fragrances"
          id={f.id}
          label="Edit"
          srLabel={'Edit ' + f.name}
          onSaved={onSaved}
          fields={[
            { key: 'name', label: 'Fragrance name' },
            { key: 'concentration', label: 'Concentration' },
          ]}
        />
      )}
    </div>
  );
}
function BrandPage() {
  const { slug } = useParams(),
    initial = useContext(Context),
    [params] = useSearchParams(),
    location = useLocation();
  const brand = initial.brands.find((b) => b.slug === slug);
  const page = Math.max(1, Math.min(10000, Number.parseInt(params.get('page') ?? '1', 10) || 1));
  const landingUrl = (brandSlug: string, pageNumber: number) =>
    '/api/v1/brands/' +
    encodeURIComponent(brandSlug) +
    (pageNumber > 1 ? '?page=' + pageNumber : '');
  const bootstrap = initial.brandLanding;
  const resource = usePagedCatalogue<BrandLanding>(
    landingUrl(slug ?? '', page),
    bootstrap
      ? { url: landingUrl(bootstrap.brand.slug, bootstrap.page.page), data: bootstrap }
      : undefined,
  );
  const { stale, error, reload } = resource;
  // Another brand's landing data never stands in while the next brand loads.
  const data = resource.shown?.brand.slug === slug ? resource.shown : undefined;
  const pageCount = data ? Math.max(1, Math.ceil(data.page.total / data.page.pageSize)) : 1;
  const heading = useRef<HTMLHeadingElement>(null);
  const href = location.pathname + location.search;
  const missingPage =
    Boolean(data && !cataloguePageExists(data.page)) ||
    error === 'Catalogue page not found' ||
    (initial.error === 'Catalogue page not found' && initial.url === href);
  useEffect(() => {
    setMetadata(
      pageMetadata(href, {
        brand,
        brandSummary: data?.stats,
        error: !brand ? 'Brand not found' : missingPage ? 'Catalogue page not found' : undefined,
      }),
    );
  }, [href, brand, data, missingPage]);
  if (missingPage)
    return (
      <Notice
        title="Catalogue page not found"
        text="This page is beyond the end of the collection. Choose a fragrance from the available catalogue pages."
      />
    );
  if (!brand)
    return (
      <Notice
        title="Brand not found"
        text="Browse the brands directory to choose a fragrance house."
      />
    );
  const name = brand.name,
    items = data?.page.items ?? [];
  const concentrations = data ? concentrationSummary(data.stats) : [];
  const groups = [{ id: 'brand-directory-heading', title: 'All ' + name + ' fragrances', note: '', cards: items }];
  return (
    <div className="finder brand-catalogue">
      {data && !stale && !error && (
        <StructuredData
          brand={brand}
          brandSummary={data.stats}
          items={items.map((f) => ({
            name: fragranceIdentity(f),
            path: '/fragrances/' + encodeURIComponent(f.slug),
          }))}
        />
      )}
      <CatalogueTrail brand={brand} />
      <section className="finder-intro brand-intro">
        <h1 ref={heading}>{name} fragrances</h1>
        {data && <p>Browse {data.stats.fragrances} {name} {data.stats.fragrances === 1 ? 'fragrance' : 'fragrances'} with recorded scent profiles.</p>}
        {data && (
          <ul className="brand-stats" aria-label={name + ' in our directory'}>
            <li>
              <strong>{data.stats.fragrances}</strong>{' '}
              {data.stats.fragrances === 1 ? 'fragrance' : 'fragrances'}
            </li>
            {concentrations.length > 0 && <li>{concentrations.join(' / ')}</li>}
          </ul>
        )}
        <Link className="text-link" to="/notes">Explore perfume notes</Link>
      </section>
      {!data ? (
        error ? (
          <p role="alert">
            {error} <button onClick={reload}>Try again</button>
          </p>
        ) : (
          <p className="loading-state" role="status">
            Loading fragrances…
          </p>
        )
      ) : (
        <>
          {groups.map((group) => (
            <section className="brand-section" key={group.id} aria-labelledby={group.id}>
              <div className="brand-section-heading">
                <h2 id={group.id}>{group.title}</h2>
                {group.note && <p>{group.note}</p>}
              </div>
              <div
                className={'product-grid brand-grid' + (stale ? ' is-loading' : '')}
                aria-busy={stale}
              >
                {group.cards.map((f) => (
                  <BrandFragranceCard key={f.id} fragrance={f} onSaved={reload} />
                ))}
              </div>
            </section>
          ))}
          {!items.length && (
            <p>
              No fragrances on this page.{' '}
              <Link to={'/brands/' + encodeURIComponent(brand.slug)}>Return to the first page</Link>
              .
            </p>
          )}
          {pageCount > 1 && (
            <Pagination
              page={Math.min(page, pageCount)}
              pageCount={pageCount}
              total={data.page.total}
              busy={stale}
              onNavigate={() => heading.current?.scrollIntoView({ block: 'start' })}
            />
          )}
          </>
      )}
    </div>
  );
}
const comparisonLink = (fragranceSlug: string, comparison: Comparison) =>
  comparison.sharedParents?.length
    ? siblingComparisonPath(fragranceSlug, comparison.dupe.slug)
    : comparisonPath(fragranceSlug, comparison.dupe.slug);
// A badge has to help someone choose, so the plate carries a ranking this page can actually support
// and nothing at all when it cannot. Each one is measured from the records already on the page - the
// accord estimates, or the checked prices - never from an editor's endorsement, which stays with the
// separate Recommended disclosure below.
type DecisionBadge = { kind: 'value' | 'accent'; label: string; detail: string };
// Only accords with a graceful comparative earn the third badge; the rest go unbadged rather than
// being forced into a phrase nobody would write.
const accentLabels: Record<string, string> = {
  fruity: 'Fruitier',
  sweet: 'Sweeter',
  'warm spicy': 'Spicier',
  woody: 'Woodier',
  smoky: 'Smokier',
  fresh: 'Fresher',
  citrus: 'Brighter citrus',
  floral: 'More floral',
  leather: 'More leather',
  vanilla: 'More vanilla',
  creamy: 'Creamier',
  green: 'Greener',
  powdery: 'Powderier',
  musky: 'Muskier',
  amber: 'More amber',
  boozy: 'Boozier',
  tobacco: 'More tobacco',
  oud: 'More oud',
  coffee: 'More coffee',
  chocolate: 'More chocolate',
  caramel: 'More caramel',
  coconut: 'More coconut',
  rose: 'More rose',
  aquatic: 'More aquatic',
};
function decisionBadges(
  original: Detail,
  alternatives: Comparison[],
  country: string,
): Map<string, DecisionBadge> {
  const badges = new Map<string, DecisionBadge>();
  // A ranking needs something to rank against, so every badge wants at least two candidates it can
  // measure and one clear winner among them; a tie leaves the plates bare.
  const winner = <T extends { id: string }>(ranked: T[], score: (row: T) => number) =>
    ranked.length >= 2 && score(ranked[0]) < score(ranked[1]) ? ranked[0] : null;
  // Lowest price per millilitre among links that state a bottle size and carry a price checked
  // recently enough to still stand. Anything undated or sizeless cannot be compared. The label names
  // the measure itself rather than a verdict like "best value", which the page could not substantiate.
  const prices = (isChanelFragrance(original) ? [] : alternatives.filter((r) => !isChanelFragrance(r.dupe))).flatMap((r) => {
    const perMl = r.dupe.offers
      .filter(
        (item: Offer) =>
          item.country === country &&
          hasRecordedPrice(item) &&
          recentlyChecked(item.priceCheckedAt, PRICE_MAX_AGE_MS) &&
          item.bottleSizeMl &&
          item.bottleSizeMl > 0,
      )
      .map((item: Offer) => ({
        perMl: Number(item.price) / item.bottleSizeMl!,
        currency: item.currency!,
        retailer: item.retailer.name,
      }))
      .sort((a, b) => a.perMl - b.perMl)[0];
    return perMl ? [{ id: r.id, ...perMl }] : [];
  });
  // Two prices in different currencies are not a comparison, so value ranks only within one.
  const oneCurrency =
    prices.length >= 2 && prices.every((row) => row.currency === prices[0].currency);
  const value = oneCurrency
    ? winner(
        [...prices].sort((a, b) => a.perMl - b.perMl),
        (row) => row.perMl,
      )
    : null;
  // The detail carries the figure, the retailer and what it was ranked against, so the claim can be
  // checked from the page it appears on - including a pair page that shows only one alternative.
  if (value && !badges.has(value.id)) {
    const perMl = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: value.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value.perMl);
    badges.set(value.id, {
      kind: 'value',
      label: 'Lowest price per ml',
      detail:
        perMl +
        ' per ml at ' +
        value.retailer +
        ': the lowest price checked in the last 24 hours among the ' +
        prices.length +
        ' alternatives to ' +
        original.name +
        ' with a price and bottle size. Prices change; check the retailer.',
    });
  }
  // What this one brings that the others do not: its single largest lift over the original, where
  // that lift is a clear two points and the biggest any alternative here offers on that accord.
  const accents = alternatives
    .flatMap((r) => {
      if (!profileSupported(original.scentProfile) || !profileSupported(r.dupe.scentProfile))
        return [];
      const top = compareAccords(original.scentProfile, r.dupe.scentProfile, 12)
        .filter(
          (row) => row.alternative! - row.original! >= 2 && accentLabels[canonicalAccord(row.name)],
        )
        .sort((a, b) => b.alternative! - b.original! - (a.alternative! - a.original!))[0];
      return top
        ? [
            {
              id: r.id,
              lift: top.alternative! - top.original!,
              label: accentLabels[canonicalAccord(top.name)],
            },
          ]
        : [];
    })
    .sort((a, b) => b.lift - a.lift);
  for (const row of accents)
    if (
      !badges.has(row.id) &&
      !accents.some((other) => other.label === row.label && badges.has(other.id))
    ) {
      badges.set(row.id, {
        kind: 'accent',
        label: row.label,
        detail: 'Its clearest lift over the compared fragrance on the recorded estimates.',
      });
      break;
    }
  return badges;
}
const badgeIcons = { value: TagIcon, accent: DropIcon } as const;
function DecisionTag({ badge, showDetail = false }: { badge: DecisionBadge; showDetail?: boolean }) {
  const Icon = badgeIcons[badge.kind];
  const tag = (
    <span
      className={'preview-tag preview-tag--' + badge.kind}
      title={showDetail ? undefined : badge.detail}
    >
      <Icon size={15} weight="bold" aria-hidden="true" />
      {badge.label}
      {!showDetail && <span className="sr-only">. {badge.detail}</span>}
    </span>
  );
  // Where there is room, the basis for the mark is printed beneath it rather than left to a tooltip
  // that touch screens never show.
  return showDetail ? (
    <div className="pair-mark">
      {tag}
      <p className="pair-mark-detail">{badge.detail}</p>
    </div>
  ) : (
    tag
  );
}
// The overview card answers "how does it differ" in scannable lines rather than a run of keywords:
// one accord per row, the direction drawn as well as named. The wording and the underlying estimates
// are the ones the comparison table below uses; nothing new is asserted here.
const traitIcons = { stronger: ArrowUp, lighter: ArrowDown, similar: Equal } as const;
// Resolve ties in favour of the broad character cues shoppers can absorb most readily in one line.
const verdictPriority: Record<string, number> = {
  fruity: 2,
  sweet: 2,
  citrus: 1,
  floral: 1,
  woody: 1,
};
export function ComparisonTraits({
  original,
  alternative,
}: {
  original: ComparisonProps['original']['profile'];
  alternative: ComparisonProps['alternative']['profile'];
}) {
  const direction = (mine: number, theirs: number) =>
    mine > theirs ? 'stronger' : mine < theirs ? 'lighter' : 'similar';
  const compared = compareAccords(original, alternative, 12);
  const rows = compared.slice(0, 3).map((row) => {
    const way = direction(row.alternative!, row.original!),
      facet = accordFacet(row.name);
    return {
      key: row.name,
      way,
      label: way === 'similar' ? 'Similar ' + facet : comparisonRowPhrase(row.name, way),
      note: '',
    };
  });
  // Performance bands are editorial estimates on both sides. Name the direction and retain the
  // alternative's recorded band so "shorter" or "softer" never stands alone as a vague claim.
  const longevitySteps = performanceSteps.longevity as readonly string[];
  const mine = longevitySteps.indexOf(alternative?.longevity ?? ''),
    theirs = longevitySteps.indexOf(original?.longevity ?? '');
  if (mine >= 0 && theirs >= 0) {
    const way = direction(mine, theirs);
    rows.push({
      key: 'longevity',
      way,
      label:
        way === 'stronger'
          ? 'Longer longevity'
          : way === 'lighter'
            ? 'Shorter longevity'
            : 'Similar longevity',
      note: 'est. ' + longevityBands[alternative!.longevity!],
    });
  }
  const projectionSteps = performanceSteps.projection as readonly string[];
  const projectionMine = projectionSteps.indexOf(alternative?.projection ?? ''),
    projectionTheirs = projectionSteps.indexOf(original?.projection ?? '');
  if (projectionMine >= 0 && projectionTheirs >= 0) {
    const way = direction(projectionMine, projectionTheirs);
    rows.push({
      key: 'projection',
      way,
      label:
        way === 'stronger'
          ? 'Stronger projection'
          : way === 'lighter'
            ? 'Softer projection'
            : 'Similar projection',
      note: 'est. ' + alternative!.projection!.toLowerCase(),
    });
  }
  if (rows.length) {
    const differences = [...compared]
      .filter((row) => row.alternative !== row.original)
      .sort(
        (a, b) =>
          Math.abs(b.alternative! - b.original!) -
          Math.abs(a.alternative! - a.original!) +
          (verdictPriority[canonicalAccord(b.name)] ?? 0) -
          (verdictPriority[canonicalAccord(a.name)] ?? 0),
      );
    const stronger = differences.find((row) => row.alternative! > row.original!);
    const lighter = differences.find((row) => row.alternative! < row.original!);
    const selected = [stronger, lighter, ...differences]
      .filter((row, index, list) => row && list.indexOf(row) === index)
      .slice(0, 2);
    const scent = selected.map((row) =>
      comparativePhrase(
        row!.name,
        direction(row!.alternative!, row!.original!) as 'stronger' | 'lighter',
      ),
    );
    const changes = [
      ...scent,
      mine >= 0 && theirs >= 0 && mine !== theirs ? (mine > theirs ? 'longer' : 'shorter') + ' wear' : null,
      projectionMine >= 0 && projectionTheirs >= 0 && projectionMine !== projectionTheirs
        ? (projectionMine > projectionTheirs ? 'stronger' : 'softer') + ' projection'
        : null,
    ].filter((change): change is string => Boolean(change));
    const shared = compared
      .filter((row) => row.original! > 0 && row.alternative! > 0)
      .sort((a, b) => Math.min(b.original!, b.alternative!) - Math.min(a.original!, a.alternative!))
      .slice(0, 3)
      .map((row) => accordFacet(row.name));
    const sharedText = shared.length < 3
      ? shared.join(' and ')
      : shared.slice(0, -1).join(', ') + ' and ' + shared.at(-1);
    const sharedFeatures = shared.length ? 'shared accords of ' + sharedText : '';
    const equalPerformance = [
      mine >= 0 && mine === theirs ? 'wear' : null,
      projectionMine >= 0 && projectionMine === projectionTheirs ? 'projection' : null,
    ].filter(Boolean);
    const verdict = changes.length
      ? changes.join(', ').replace(/^./, (letter) => letter.toUpperCase()) +
        (sharedFeatures ? ', with ' + sharedFeatures : '') + '.'
      : shared.length
        ? 'Shared accords include ' + sharedText +
          (equalPerformance.length ? ', with the same estimated ' + equalPerformance.join(' and ') + ' band' : '') + '.'
        : equalPerformance.length
          ? 'The same estimated ' + equalPerformance.join(' and ') + ' band is recorded for both.'
          : 'No specific difference is recorded in the compared fields.';
    return (
      <div className="preview-comparison">
        <p className="preview-verdict">{verdict}</p>
        <ul className="preview-traits" aria-label="How the fragrances differ">
          {rows.map((row) => {
            const Icon = traitIcons[row.way as keyof typeof traitIcons];
            return (
              <li className={'preview-trait preview-trait--' + row.way} key={row.key}>
                <Icon size={16} strokeWidth={2.2} aria-hidden="true" />
                <span>
                  {row.label}
                  {row.note ? <span className="preview-trait-note"> · {row.note}</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
  // Without estimates on both sides there is no comparison to draw, so the card says so and falls
  // back to whatever this fragrance alone has on record.
  const own = (alternative?.accords ?? [])
    .filter((accord) => accord.intensity !== 0)
    .slice(0, 3)
    .map((accord) => accord.name);
  return (
    <p className="preview-traits-empty">
      {own.length
        ? 'Recorded accords: ' + own.join(', ') + '. No side-by-side comparison yet.'
        : 'Scent comparison not yet recorded.'}
    </p>
  );
}
// Where the estimates above come from, in the card's own quietest voice. An unassessed pairing says
// nothing rather than implying a grade.
function ComparisonEvidence({ profile }: { profile: ComparisonProps['alternative']['profile'] }) {
  if (
    !profile?.accords.length ||
    !profile.reviewedAt ||
    !Number.isFinite(Date.parse(profile.reviewedAt))
  )
    return null;
  return (
    <p className="preview-evidence">
      Editorial estimate · reviewed{' '}
      {new Date(profile.reviewedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      })}
    </p>
  );
}
export function OverviewBuyingLink({
  offers,
  country,
  countryName,
  originalId,
  affiliate,
  children,
}: {
  offers: Offer[];
  country: string;
  countryName?: string;
  originalId: string;
  affiliate: boolean | ClickTimeAffiliates;
  children?: ReactNode;
}) {
  // Without a link the block still holds its place at the foot of the card, so the actions of three
  // cards line up on one baseline however much sits above them.
  const heading = <h4 className="preview-buying-label">Buying information</h4>;
  const empty = (message: string, note: string) => (
    <div className="preview-buying">
      {heading}
      <p className="preview-buying-empty">{message}</p>
      <p className="preview-note">{note}</p>
      {children ? <div className="preview-actions">{children}</div> : null}
    </div>
  );
  if (!country)
    return empty(
      'Choose your country',
      'Set your country in the page header to see a buying link and its recorded price.',
    );
  const deliveryConfirmed = (item: Offer) =>
    item.country === country &&
    item.deliveryStatus === 'SUPPORTED' &&
    Boolean(item.shippingEvidenceUrl) &&
    recentlyChecked(item.deliveryCheckedAt, DELIVERY_MAX_AGE_MS);
  // Delivery evidence takes precedence over product-link verification. Preserve editorial
  // order for equally supported offers, without changing the supplied array.
  const priority = (item: Offer) =>
    (deliveryConfirmed(item) ? 4 : 0) +
    (item.country === country ? 2 : 0) +
    (item.verificationStatus === 'VERIFIED' ? 1 : 0);
  const offer = offers
    .filter(
      (item) =>
        (item.country === country || item.country === null) &&
        item.deliveryStatus !== 'UNSUPPORTED',
    )
    .sort((a, b) => priority(b) - priority(a))[0];
  if (!offer)
    return empty(
      'No buying link yet',
      'A retailer appears here once a link that delivers to ' +
        (countryName ?? country) +
        ' has been checked.',
    );
  const params = new URLSearchParams({ country, from: originalId });
  const priced = !offer.amazon && hasRecordedPrice(offer);
  const fresh = recentlyChecked(offer.priceCheckedAt, PRICE_MAX_AGE_MS);
  const affiliateLink = earnsCommission(offer.url, clickTimeAffiliates(affiliate), offer.affiliate);
  const checkedDate = (value: string) =>
    new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  const bottleSize =
    offer.bottleSizeMl && offer.bottleSizeMl > 0 ? offer.bottleSizeMl + ' ml' : null;
  // The recorded price is a card-level fact with its date beside it, never a live quote, so the
  // button beneath it stays a plain instruction to go and check.
  const priceChecked =
    priced && offer.priceCheckedAt ? 'price checked ' + checkedDate(offer.priceCheckedAt) : null;
  return (
    <div className="preview-buying">
      <div className="preview-buying-heading">
        {heading}
        <div className="preview-badges">
          <VerificationBadge status={offer.verificationStatus} />
          {offer.officialStore && <OfficialStoreBadge />}
        </div>
      </div>
      <div className="preview-price-row">
        {offer.amazon ? (
          <p className="preview-price">
            <span className="preview-price-none">Check price on Amazon</span>
          </p>
        ) : priced ? (
          <p className="preview-price">
            <strong>
              {new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: offer.currency!,
              }).format(Number(offer.price))}
            </strong>
            <span>{offer.currency}</span>
            {fresh && offer.availability === 'OUT_OF_STOCK' ? (
              <span className="preview-stock">Out of stock when checked</span>
            ) : null}
          </p>
        ) : (
          <p className="preview-price">
            <span className="preview-price-none">Price not recorded</span>
          </p>
        )}
      </div>
      <p className="preview-retailer">
        {bottleSize ? (
          <strong className="preview-bottle-size">{bottleSize}</strong>
        ) : (
          <span>Bottle size not recorded</span>
        )}
        {priceChecked ? (
          <>
            <span className="preview-meta-separator" aria-hidden="true">
              ·
            </span>
            <span>{priceChecked}</span>
          </>
        ) : null}
      </p>
      <OfferPromotion offer={offer} />
      <div className="preview-actions">
        {children}
        <a
          className="buying-button"
          href={'/go/offer/' + offer.id + '?' + params}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
        >
          <span>{buyingLinkTitle(offer)}</span>
          <span className="sr-only">: check the price</span>
        </a>
      </div>
      <p className="preview-note">
        {deliveryConfirmed(offer) ? (
          <>
            Delivery to {countryName ?? country} supported ·{' '}
            <a href={offer.shippingEvidenceUrl!} target="_blank" rel="noopener noreferrer">
              policy checked {checkedDate(offer.deliveryCheckedAt!)}
            </a>
            .
          </>
        ) : (
          <>Delivery to {countryName ?? country} not confirmed.</>
        )}{' '}
        Confirm stock, price and delivery charges with the retailer.
        {affiliateLink ? ' ' + affiliateDisclosure + '.' : ''}
      </p>
    </div>
  );
}
function SimilarAccordsCarousel({
  fragrances,
  name,
  onSaved,
}: {
  fragrances: NonNullable<Detail['similarFragrances']>;
  name: string;
  onSaved: () => void;
}) {
  const track = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  useEffect(() => {
    const node = track.current;
    if (!node) return;
    const update = () =>
      setEdges({
        start: node.scrollLeft <= 1,
        end: node.scrollLeft + node.clientWidth >= node.scrollWidth - 1,
      });
    update();
    node.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => {
      node.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [fragrances]);
  const move = (direction: number) => {
    const node = track.current;
    if (node)
      node.scrollBy({
        left: direction * node.clientWidth,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'instant'
          : 'smooth',
      });
  };
  return (
    <section
      className="similar-accords"
      aria-labelledby="similar-accords-heading"
      aria-roledescription="carousel"
    >
      <div className="similar-accords-heading">
        <div>
          <h2 id="similar-accords-heading">Fragrances with similar accords</h2>
          <p>Scents that share accords with {name}, matched automatically across the catalogue.</p>
        </div>
        {fragrances.length > 0 && (
          <div className="similar-accords-controls">
            <button
              type="button"
              aria-label="Previous similar fragrances"
              aria-controls="similar-accords-track"
              disabled={edges.start}
              onClick={() => move(-1)}
            >
              <LinkArrow direction="left" size={18} />
            </button>
            <button
              type="button"
              aria-label="Next similar fragrances"
              aria-controls="similar-accords-track"
              disabled={edges.end}
              onClick={() => move(1)}
            >
              <LinkArrow size={18} />
            </button>
          </div>
        )}
      </div>
      {fragrances.length ? (
        <ul className="similar-accords-track" id="similar-accords-track" ref={track}>
          {fragrances.map((f) => (
            <li key={f.id}>
              <ProductCard fragrance={f} sharedAccords={f.sharedAccords} onSaved={onSaved} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="similar-accords-empty">
          No close accord matches are available for this fragrance yet.
        </p>
      )}
    </section>
  );
}
function fragrancePageHeading(f: Detail) {
  const identity = fragranceIdentity(f);
  return identity;
}
function ExistingComparisonCard({
  fragrance,
  comparedWith,
  href,
  preview,
  notes,
}: {
  fragrance: Fragrance;
  comparedWith: Fragrance;
  href: string;
  preview: boolean;
  notes?: string;
}) {
  const compareAction = (
    <Link className="button secondary comparison-jump" to={href}>
      Compare<span className="sr-only"> {fragrance.name} with {comparedWith.name}</span>
    </Link>
  );
  return (
    <article className="alternative-preview existing-comparison-card">
      <div className="preview-plate">
        {!preview && <SaveFragrance fragrance={fragrance} compact />}
        <Bottle fragrance={fragrance} thumb />
        <ReviewTally reviews={fragrance.reviews} />
      </div>
      <div className="preview-body">
        <div className="preview-identity">
          <p className="product-brand">{fragrance.brand?.name}</p>
          <h3><Link to={'/fragrances/' + fragrance.slug}>{fragrance.name}</Link></h3>
          <p className="concentration">
            {fragrance.concentration}
          </p>
        </div>
        <p className="existing-comparison-pair">
          Compared with <Link to={'/fragrances/' + comparedWith.slug}>{comparedWith.name}</Link>
        </p>
        <ComparisonTraits original={comparedWith.scentProfile} alternative={fragrance.scentProfile} />
        {notes?.trim() && <p className="existing-comparison-note">{notes}</p>}
        <ComparisonEvidence profile={fragrance.scentProfile} />
        <div className="preview-buying">
          <div className="preview-actions">{compareAction}</div>
        </div>
      </div>
    </article>
  );
}
// One comparison, one page, reached from either fragrance. Editorial pairs retain their recorded
// address; pairs derived from a shared comparison use a stable slug order.
export function ComparisonPage() {
  const { originalSlug, alternativeSlug } = useParams(),
    location = useLocation();
  const initial = useContext(Context),
    { country, countries } = useContext(CountryContext);
  const countryName = countries.find((c) => c.code === country)?.name;
  const [photoForm, setPhotoForm] = useState(false);
  const url =
    '/api/v1/fragrances/' +
    encodeURIComponent(originalSlug ?? '') +
    (country ? '?country=' + country : '');
  const resource = useResource<Detail>(
    url,
    initial.detail && initial.detail.slug === originalSlug
      ? { url: '/api/v1/fragrances/' + initial.detail.slug, data: initial.detail }
      : undefined,
  );
  const [selectedOther, setSelectedOther] = useState<Detail | undefined>(initial.compareWith);
  useEffect(() => {
    if (!alternativeSlug || resource.data?.alternatives.some(row => row.dupe.slug === alternativeSlug)) return;
    let active = true;
    api<Detail>('/api/v1/fragrances/' + encodeURIComponent(alternativeSlug) + (country ? '?country=' + country : ''))
      .then(value => { if (active) setSelectedOther(value); })
      .catch(() => { if (active) setSelectedOther(undefined); });
    return () => { active = false; };
  }, [alternativeSlug, resource.data, country]);
  const f = resource.data;
  const other = selectedOther;
  const r: Comparison | undefined = f?.alternatives.find((row) => row.dupe.slug === alternativeSlug) ??
    (f && other && other.slug === alternativeSlug && other.slug !== f.slug ? {
      id: 'selected:' + other.id, type: 'RELATED_COMPARISON', notes: '',
      recommended: false, recommendationReason: '', dupe: other,
    } : undefined);
  const unmatched = Boolean(f && !r);
  useEffect(() => {
    setMetadata(
      pageMetadata(location.pathname + location.search, {
        comparison:
          f && r
            ? { original: f, alternative: r.dupe, closeDupe: r.type === 'CLOSE_DUPE', editorial: !r.id.startsWith('selected:') }
            : undefined,
        error:
          resource.error && !f
            ? 'Comparison unavailable'
            : unmatched
              ? 'Comparison not found'
              : !f
                ? 'Loading comparison'
                : undefined,
      }),
    );
  }, [f, r, unmatched, resource.error, location.pathname, location.search]);
  if (resource.error && !f) return <Notice title="Comparison unavailable" text={resource.error} />;
  if (!f)
    return (
      <p className="loading-state" role="status">
        Loading comparison…
      </p>
    );
  if (!r)
    return (
      <Notice
        title="Comparison not found"
        text="We have not published a comparison between these two fragrances."
      />
    );
  // The mark this alternative earns among the original's others, awarded by the same ranking the
  // overview uses, so one fragrance carries one mark wherever it is shown.
  const badge = decisionBadges(f, f.alternatives, country).get(r.id);
  // Each side is the same identity card in the same order: the bottle beside its name, what it is
  // made of and how it reads, then one way out to the fragrance itself, so the pair reads across
  // without the photography pushing a name or a relationship out of view.
  const side = (fragrance: Fragrance, contribute: boolean) => {
    // The accords it leads with, strongest first. A zero is a recorded absence, not a keyword.
    const accords = (fragrance.scentProfile?.accords ?? [])
      .filter((a) => a.intensity === null || a.intensity > 0)
      .slice()
      .sort((a, b) => (b.intensity ?? 0) - (a.intensity ?? 0))
      .slice(0, 4);
    const notes = (fragrance.keyNotes ?? []).slice(0, 5);
    return (
      <article className={'pair-side pair-side--' + (contribute ? 'alternative' : 'original')}>
        <div className="pair-product">
          <div className={'pair-photo' + (fragrance.media ? '' : ' pair-photo--empty')}>
            <Bottle fragrance={fragrance} thumb>
              {contribute && (
                <ContributePhotoButton fragrance={fragrance} onClick={() => setPhotoForm(true)} />
              )}
            </Bottle>
            <ReviewTally reviews={fragrance.reviews} />
          </div>
          <div className="pair-copy">
            <p className="product-brand">{fragrance.brand?.name}</p>
            <h2>
              <Link to={'/fragrances/' + fragrance.slug}>{fragrance.name}</Link>
            </h2>
            <p className="concentration">
              {fragrance.concentration}
            </p>
            {(notes.length > 0 || accords.length > 0) && (
              <dl className="pair-facts">
                {notes.length > 0 && (
                  <div className="pair-fact">
                    <dt>Key notes</dt>
                    <dd>
                      <ul className="pair-notes">
                        {notes.map((note) => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                {accords.length > 0 && (
                  <div className="pair-fact">
                    <dt>Accords</dt>
                    <dd>
                      <ul className="overview-keywords pair-accords">
                        {accords.map((accord) => (
                          <li key={accord.name}>{accord.name}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </div>
        <footer className="pair-foot">
          {contribute && badge && <DecisionTag badge={badge} showDetail />}
          <div className="pair-actions">
            <Link className="text-link pair-link" to={'/fragrances/' + fragrance.slug}>
              View fragrance<span className="sr-only"> {fragrance.name}</span>
            </Link>
            <SaveFragrance fragrance={fragrance} compact />
          </div>
        </footer>
      </article>
    );
  };
  return (
    <div className="detail-page comparison-page" aria-busy={resource.loading}>
      <CatalogueTrail brand={f.brand} fragrance={f} comparison />
      {r.dupe.offers.some(offer => earnsCommission(offer.url, clickTimeAffiliates(initial.affiliateLinks ?? false), offer.affiliate)) &&
        <p className="affiliate-note">{affiliateDisclosure} <Link to="/about#affiliate">How we make money</Link></p>}
      <header className="comparison-head">
        <h1>
          <span className="versus-name alternative">{r.dupe.name}</span>
          <span className="versus-mark"> vs </span>
          <span className="versus-name original">{f.name}</span>
        </h1>
        {!f.isOriginal && r.recommended && <RecommendationBadge reason={r.recommendationReason} />}
        <p className="comparison-head-note">
          {r.sharedParents?.length
            ? 'Similar scent direction. Compare reported notes, accords and differences.'
            : r.notes?.trim() || r.dupe.description}
        </p>
      </header>
      {/* The alternative leads, as it does in the title above: the page is about it, and the pair reads
      left to right in the same order the heading names them. */}
      <div className="comparison-pair">
        {side(r.dupe, true)}
        {side(f, false)}
      </div>
      <PageImageActions
        key={r.dupe.id + String(r.dupe.media?.id)}
        fragrance={r.dupe}
        onSaved={resource.reload}
      />
      <ImageSubmissionForm
        key={r.dupe.id}
        fragrance={r.dupe}
        open={photoForm}
        onClose={() => setPhotoForm(false)}
      />
      {/* The evidence leads and buying stays beside it, the same division of a band the fragrance page
      uses, so a comparison never ends at a retailer before it has been read. */}
      <div className="comparison-main">
        <div className="comparison-reading">
          <ComparisonDetails
            comparisonNotes={r.notes}
            relationshipType={r.type}
            original={{ id: f.id, name: f.name, profile: f.scentProfile }}
            alternative={{ id: r.dupe.id, name: r.dupe.name, profile: r.dupe.scentProfile }}
            onSaved={resource.reload}
          />
          <EthicalTags fragrance={r.dupe} />
          <EditActions>
            {!r.sharedParents?.length && <InlineEdit
              entity="relationships"
              id={r.id}
              label="Edit comparison description"
              srLabel={'Edit how ' + r.dupe.name + ' compares to ' + f.name}
              onSaved={resource.reload}
              fields={[{ key: 'notes', label: 'How it compares to ' + f.name, type: 'textarea' }]}
            />}
            <InlineEdit
              entity="fragrances"
              id={r.dupe.id}
              label="Edit fragrance"
              srLabel={'Edit ' + r.dupe.name}
              onSaved={resource.reload}
              fields={[
                { key: 'name', label: 'Fragrance name' },
                { key: 'concentration', label: 'Concentration' },
                { key: 'description', label: 'Public description', type: 'textarea' },
                ...ethicalFields,
              ]}
            />
            {!r.sharedParents?.length && <DeleteRecord
              entity="relationships"
              id={r.id}
              label="Delete comparison"
              what={'the comparison with ' + r.dupe.name}
              onDeleted={resource.reload}
            />}
          </EditActions>
        </div>
        {/* Chanel pairs carry no buying links. Where those links would have been for a Chanel
            product, the independence notice takes their place. */}
        {isChanelFragrance(r.dupe) ? (
          <aside className="comparison-aside">
            <ChanelDisclosure className="comparison-buying" />
          </aside>
        ) : !isChanelFragrance(f) && (
          <aside className="comparison-aside">
            <section className="comparison-buying" aria-labelledby="comparison-buying-heading">
              <h2 id="comparison-buying-heading">Where to buy {r.dupe.name}</h2>
              <BuyingLinks
                fragrance={r.dupe}
                offers={r.dupe.offers}
                countryName={countryName}
                countries={countries}
                selectedCountry={country}
                fragranceId={r.dupe.id}
                originalId={f.id}
                affiliate={initial.affiliateLinks ?? false}
                onSaved={resource.reload}
              />
            </section>
          </aside>
        )}
      </div>
      {/* Where neither of these two is the answer, the next places to look. The list already excludes
      every alternative linked to the original, so it never repeats what this page has shown. It is
      left out entirely when there is nothing to show, rather than ending the page on an empty shelf. */}
      {f.similarFragrances?.length ? (
        <SimilarAccordsCarousel
          key={f.id}
          fragrances={f.similarFragrances}
          name={f.name}
          onSaved={resource.reload}
        />
      ) : null}
      <BackToTop />
    </div>
  );
}
export function FragrancePage({ preview = false }: { preview?: boolean }) {
  const { slug } = useParams(),
    location = useLocation(),
    initial = useContext(Context),
    { country, countries } = useContext(CountryContext);
  const url =
    (preview ? '/api/v1/admin/preview/' : '/api/v1/fragrances/') +
    encodeURIComponent(slug ?? '') +
    (country ? '?country=' + country : '');
  const resource = useResource<Detail>(
    url,
    !preview && initial.detail
      ? { url: '/api/v1/fragrances/' + initial.detail.slug, data: initial.detail }
      : undefined,
  );
  const f = resource.data,
    countryName = countries.find((c) => c.code === country)?.name;
  const [photoFormFor, setPhotoFormFor] = useState<string | null>(null);
  const [imageSlot, setImageSlot] = useState<HTMLDivElement | null>(null);
  const linkedOriginals = f?.originals ?? [],
    comparisonCount = f ? f.alternatives.length + linkedOriginals.length : 0;
  // Each fragrance can appear on either side of a published comparison. Show both directions.
  useEffect(() => {
    setMetadata(
      pageMetadata(location.pathname + location.search, {
        detail: f,
        error: resource.error && !f ? 'Fragrance not found' : !f ? 'Loading fragrance' : undefined,
      }),
    );
  }, [f, resource.error, location.pathname, location.search]);
  if (resource.error && !f) return <Notice title="Fragrance unavailable" text={resource.error} />;
  if (
    !f &&
    initial.error &&
    new URL(initial.url, 'http://localhost').pathname === '/fragrances/' + slug
  )
    return <Notice title="Fragrance unavailable" text={initial.error} />;
  if (!f)
    return (
      <p className="loading-state" role="status">
        Loading fragrance…
      </p>
    );
  const badges = decisionBadges(f, f.alternatives, country);
  return (
    <div className="detail-page">
      {!preview && (
        <StructuredData detail={f} brand={initial.brands.find((b) => b.slug === f.brand?.slug)} />
      )}{' '}
      {preview && (
        <div className="preview-banner">
          Private draft preview. Only signed-in administrators can see this page.
        </div>
      )}
      <CatalogueTrail brand={f.brand} fragrance={f} />
      <div className="comparison-layout" aria-busy={resource.loading}>
        <section className="original-panel">
          <p className="section-label">Fragrance</p>
          <div className="original-photo">
            <Bottle fragrance={f}>
              {!preview && (
                <ContributePhotoButton fragrance={f} onClick={() => setPhotoFormFor(f.id)} />
              )}
              <div className="image-slot-actions" ref={setImageSlot} />
            </Bottle>
          </div>
          <div className="original-copy">
            <div className="fragrance-identity">
              <p className="product-brand">{f.brand?.name}</p>
              <div className="fragrance-title-row">
                <h1>{fragrancePageHeading(f)}</h1>
                {!preview && <SaveFragrance fragrance={f} compact />}
              </div>
              <p className="concentration">{f.concentration}</p>
            </div>
            {f.media?.id.startsWith('ai-') && (
              <p className="ai-image-note">AI-generated illustration. Actual bottle and packaging may differ.</p>
            )}
            <p className="description">{f.description}</p>
            {verifiedFragranceNotes[f.slug] && (
              <section className="maker-notes" aria-label="Fragrance notes listed by the maker">
                <h2>Notes listed by the maker</h2>
                <dl>
                  {verifiedFragranceNotes[f.slug].groups.map((group) => (
                    <div key={group.label}>
                      <dt>{group.label}</dt>
                      <dd>{group.notes.join(' · ')}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
            <FragranceScentRatings profile={f.scentProfile} name={f.name} />
            <EthicalTags fragrance={f} />
            <EditActions>
              <InlineEdit
                entity="fragrances"
                id={f.id}
                label="Edit this fragrance"
                onSaved={resource.reload}
                fields={[
                  { key: 'name', label: 'Fragrance name' },
                  { key: 'concentration', label: 'Concentration' },
                  { key: 'description', label: 'Public description', type: 'textarea' },
                  ...ethicalFields,
                ]}
              />
              {f.brand && (
                <InlineEdit
                  entity="brands"
                  id={f.brand.id}
                  label="Edit brand"
                  onSaved={resource.reload}
                  fields={[{ key: 'name', label: 'Brand name' }]}
                />
              )}
            </EditActions>
          </div>
          {isChanelFragrance(f) ? (
            <ChanelDisclosure className="fragrance-prices" />
          ) : (
            <FragrancePrices
              fragranceId={f.id}
              onSaved={resource.reload}
              offers={f.offers ?? []}
              country={country}
              countryName={countryName}
              affiliate={initial.affiliateLinks ?? false}
            />
          )}
          <PageImageActions
            key={f.id + String(f.media?.id)}
            fragrance={f}
            onSaved={resource.reload}
            triggerSlot={f.media ? null : imageSlot}
          />
          {!preview && !f.media && (
            <div className="fragrance-photo-contribution">
              <ImageSubmissionForm
                key={f.id}
                fragrance={f}
                open={photoFormFor === f.id}
                onClose={() => setPhotoFormFor(null)}
              />
            </div>
          )}
        </section>
        <section className="alternatives-panel">
          <div className="alternatives-heading">
            <h2>Editors’ picks: similar accords</h2>
            <span>
              {comparisonCount} {comparisonCount === 1 ? 'comparison' : 'comparisons'}
            </span>
          </div>
          <EditActions>
              <InlineEdit
                entity="relationships"
                icon="plus"
                label="Add a comparison"
                onSaved={resource.reload}
                base={{
                  originalId: f.id,
                  dupeId: '',
                  type: 'INSPIRED_ALTERNATIVE',
                  status: 'DRAFT',
                  notes: '',
                  rawNotes: '',
                  recommended: false,
                  recommendationReason: '',
                  sortOrder: f.alternatives.length,
                  sources: [],
                  verifiedAt: null,
                }}
                fields={[
                  {
                    key: 'dupeId',
                    label: 'Compared fragrance',
                    type: 'select',
                    empty: 'Choose an existing fragrance…',
                    options: async () => {
                      const { fragrances } = await api<{ fragrances: Record<string, any>[] }>(
                        '/api/v1/admin/lookup?comparisonOriginalId=' + encodeURIComponent(f.id),
                      );
                      return fragrances.map((x) => ({
                        value: x.id,
                        label: fragranceOptionLabel(x) + ' (' + x.status.toLowerCase() + ')',
                      }));
                    },
                    extra: ({ set, addOption }) => (
                      <QuickAddFragrance
                        label="Add a new fragrance"
                        onCreated={(row) => {
                          addOption('dupeId', {
                            value: row.id,
                            label: fragranceOptionLabel(row) + ' (draft)',
                          });
                          set('dupeId', row.id);
                        }}
                      />
                    ),
                  },
                  {
                    key: 'type',
                    label: 'Relationship type',
                    type: 'select',
                    empty: null,
                    options: [
                      { value: 'CLOSE_DUPE', label: 'Strong scent profile overlap' },
                      { value: 'INSPIRED_ALTERNATIVE', label: 'Shares a scent direction' },
                      { value: 'RELATED_COMPARISON', label: 'Related comparison' },
                    ],
                  },
                  { key: 'sortOrder', label: 'Display order', type: 'number' },
                  {
                    key: 'status',
                    label: 'Publication status',
                    type: 'select',
                    empty: null,
                    options: [
                      { value: 'DRAFT', label: 'Draft' },
                      { value: 'PUBLISHED', label: 'Published' },
                      { value: 'ARCHIVED', label: 'Archived' },
                    ],
                    extra: () => (
                      <p className="field-help">
                        Publishing needs both fragrances published first, so a fragrance added
                        here has to be finished in the workspace before its comparison can go live.
                      </p>
                    ),
                  },
                ]}
                done={(row) => (
                  <>
                    <p className="save-message">
                      <Check size={15} aria-hidden="true" />
                      {row.status === 'PUBLISHED'
                        ? 'Published. It now appears in the list below.'
                        : 'Saved as a draft. It stays off this page until the fragrance and the comparison are both published.'}
                    </p>
                    <p className="field-help">
                      A fragrance can be published once it has a brand, a description of at least 30
                      characters, a verified identity with its date and source, and an approved
                      image.
                    </p>
                    <div className="inline-edit-links">
                      <Link className="text-link" to={'/admin/fragrances/' + row.dupeId}>
                        Finish the fragrance
                      </Link>
                      <Link className="text-link" to={'/admin/relationships/' + row.id}>
                        Open the comparison
                      </Link>
                    </div>
                  </>
                )}
              />
          </EditActions>
          <p className="comparison-explainer">
            Pairs drawn from our editorial pairings, compared on estimated accords, strength and wear.
          </p>
          {!comparisonCount && (
            <div className="empty-state">
              <h3>No published comparisons yet.</h3>
              <p>We’re still reviewing comparisons for this fragrance.</p>
            </div>
          )}
          {comparisonCount > 0 && (
            <div className="alternatives-overview">
              {f.alternatives.map((r) => (
                <article className="alternative-preview" key={r.id}>
                  <div className="preview-plate">
                    {badges.get(r.id) ? <DecisionTag badge={badges.get(r.id)!} /> : null}
                    {!preview && <SaveFragrance fragrance={r.dupe} compact />}
                    <Bottle fragrance={r.dupe} thumb />
                    <ReviewTally reviews={r.dupe.reviews} />
                  </div>
                  <div className="preview-body">
                    <div className="preview-identity">
                      <p className="product-brand">{r.dupe.brand?.name}</p>
                      <h3>
                        <Link to={'/fragrances/' + r.dupe.slug}>{r.dupe.name}</Link>
                      </h3>
                      <p className="concentration">
                        {r.dupe.concentration}
                      </p>
                    </div>
                    {!!r.sharedParents?.length && (
                      <p className="existing-comparison-pair">Similar scent direction</p>
                    )}
                    {!f.isOriginal && r.recommended && <RecommendationBadge reason={r.recommendationReason} />}
                    <ComparisonTraits
                      original={f.scentProfile}
                      alternative={r.dupe.scentProfile}
                    />
                    <ComparisonEvidence profile={r.dupe.scentProfile} />
                    {/* Chanel pairs carry no buying links; a Chanel alternative shows the
                        independence notice where its links would have been. */}
                    {isChanelFragrance(r.dupe) ? (
                      <ChanelDisclosure compact>
                        <Link className="button secondary comparison-jump" to={comparisonLink(f.slug, r)}>
                          Compare<span className="sr-only"> {r.dupe.name} with {f.name}</span>
                        </Link>
                      </ChanelDisclosure>
                    ) : isChanelFragrance(f) ? (
                      <div className="preview-buying">
                        <div className="preview-actions">
                          <Link className="button secondary comparison-jump" to={comparisonLink(f.slug, r)}>
                            Compare<span className="sr-only"> {r.dupe.name} with {f.name}</span>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <OverviewBuyingLink
                        offers={r.dupe.offers}
                        country={country}
                        countryName={countryName}
                        originalId={f.id}
                        affiliate={initial.affiliateLinks ?? false}
                      >
                        <Link
                          className="button secondary comparison-jump"
                          to={comparisonLink(f.slug, r)}
                        >
                          Compare
                          <span className="sr-only"> {r.dupe.name} with {f.name}</span>
                        </Link>
                      </OverviewBuyingLink>
                    )}
                  </div>
                </article>
              ))}
              {linkedOriginals.map((r) => (
                <ExistingComparisonCard
                  key={r.id}
                  fragrance={r.original}
                  comparedWith={f}
                  href={comparisonPath(r.original.slug, f.slug)}
                  preview={preview}
                  notes={r.notes}
                />
              ))}
            </div>
          )}
          <SimilarAccordsCarousel
            key={f.id}
            fragrances={f.similarFragrances ?? []}
            name={f.name}
            onSaved={resource.reload}
          />
        </section>
      </div>
      {!preview && reviewsVisible && <FragranceReviews fragrance={f} />}
      <BackToTop />
    </div>
  );
}
// Every panel stays in the server-rendered HTML; inactive ones are only hidden, so crawlers and
// find-in-page still reach them. Anchors inside a panel (FAQ answers, older links) open that panel.
const aboutPanels = [
  { id: 'how-it-works', label: 'How it works', anchors: [] },
  {
    id: 'comparisons',
    label: 'Reading a comparison',
    anchors: ['research', 'comparison-types', 'ratings', 'recommended'],
  },
  { id: 'affiliate', label: 'Buying links & funding', anchors: [] },
  { id: 'faq', label: 'Questions', anchors: ['testing', 'country-links', 'prices', 'purchasing'] },
] as const;
type AboutPanelId = (typeof aboutPanels)[number]['id'];
function About() {
  const location = useLocation(),
    page = useRef<HTMLElement>(null),
    { affiliateLinks } = useContext(Context);
  // Starts on the first panel to match the server render, then follows the hash before paint.
  const [active, setActive] = useState<AboutPanelId>('how-it-works');
  useLayoutEffect(() => {
    let anchor = location.hash.slice(1);
    try {
      anchor = decodeURIComponent(anchor);
    } catch {
      /* Invalid escapes can still be literal IDs. */
    }
    const panel = aboutPanels.find(
      (p) => p.id === anchor || (p.anchors as readonly string[]).includes(anchor),
    );
    setActive(panel?.id ?? 'how-it-works');
  }, [location.hash]);
  useLayoutEffect(() => {
    // React only writes a boolean hidden, so upgrade inactive panels to until-found after each change.
    page.current
      ?.querySelectorAll('[data-panel][hidden]')
      .forEach((el) => el.setAttribute('hidden', 'until-found'));
    // On narrow screens the tab row scrolls sideways; keep the selected tab within it.
    const tab = page.current?.querySelector<HTMLElement>('.about-contents [aria-current]'),
      row = tab?.parentElement;
    if (tab && row && row.scrollWidth > row.clientWidth)
      row.scrollLeft = tab.offsetLeft - row.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2;
  }, [active]);
  useEffect(() => {
    // Find-in-page reveals a matching hidden panel itself; make it the selected one.
    const root = page.current;
    if (!root) return;
    const reveal = (event: Event) => {
      const id = (event.target as HTMLElement).closest('[data-panel]')?.getAttribute('data-panel');
      if (id) setActive(id as AboutPanelId);
    };
    root.addEventListener('beforematch', reveal);
    return () => root.removeEventListener('beforematch', reveal);
  }, []);
  const panel = (id: AboutPanelId) => ({ 'data-panel': id, hidden: active !== id });
  return (
    <article className="about-page" ref={page}>
      <header className="about-heading">
        <h1>About Identifrag</h1>
        <p className="about-intro">Find a fragrance you love. Explore its comparisons.</p>
        <p>
          Identifrag is an editorial directory of fragrance comparisons. Each page connects a
          familiar perfume to fragrances with related scent characteristics, explains their shared
          notes and differences, and, where we have them, links to buy in your country. Browse
          freely; you don’t need an account.
        </p>
      </header>
      <nav className="about-contents" aria-label="About sections">
        {aboutPanels.map((p) => (
          <Link key={p.id} to={'#' + p.id} aria-current={active === p.id ? 'true' : undefined}>
            {p.label}
          </Link>
        ))}
      </nav>
      <section className="about-panel" aria-labelledby="how-it-works" {...panel('how-it-works')}>
        <h2 id="how-it-works">How it works</h2>
        <p>
          Start with a fragrance you already know. Search for it by name or a common abbreviation,
          or browse by brand, and check the concentration: an eau de toilette and an eau de parfum
          of the same name can smell and wear quite differently.
        </p>
        <p>
          Each fragrance has its own page with links to its comparisons, notes on how each pair
          compares, their accord profiles side by side and how they are reported to wear. Set your
          delivery country to see buying links for that destination. The purchase itself
          happens with the retailer, who handles payment, delivery and returns.
        </p>
      </section>
      <section className="about-panel" aria-labelledby="comparisons" {...panel('comparisons')}>
        <h2 id="comparisons">Reading a comparison</h2>
        <p id="research">
          A comparison describes two specific releases. We draw on published product information
          and community fragrance reports, and describe their recorded notes, accords and wear.
        </p>
        <h3 id="comparison-types">What comparisons show</h3>
        <p>Each pair shows recorded notes, accords and estimated wear for the two fragrances.</p>
        <h3 id="ratings">Accord scores and performance</h3>
        <p>
          Accord scores show how prominent a characteristic is, from a background trace to a
          defining feature. They are not customer ratings or percentage matches. Longevity and
          projection summarise reported wear and are editorial estimates. A profile’s review date
          refers to that assessment, not to a fresh check of the product or its buying links.
        </p>
        <h3 id="recommended">Recommended comparisons</h3>
        <p>
          An editor awards the Recommended badge to a specific comparison and states the reason
          alongside it. A higher place in a list, a buying link or a commission never earns one.
        </p>
        <p className="about-note">
          Scent and performance vary with skin, application and formulation, so a similar profile is
          not a promise of an identical experience. Read the comparison and performance notes
          together and, where you can, try a sample before buying a full bottle.
        </p>
      </section>
      <section className="about-panel" aria-labelledby="affiliate" {...panel('affiliate')}>
        <h2 id="affiliate">Buying links & funding</h2>
        <h3>What a verified link means</h3>
        <p>
          Our staff have checked the retailer and approved the link as trusted, including where it
          delivers. Coverage varies from
          country to country, so choose your delivery country to see the links that apply to you.
        </p>
        <p>
          Links marked “not yet verified” haven’t been through that check, so confirm the listing
          and delivery with the retailer. An “Official store” badge means the link goes to the
          brand’s own shop.
        </p>
        <h3>How Identifrag is funded</h3>
        <p>
          Affiliate commissions fund Identifrag. Some buying links are affiliate links: if you buy
          through one, we may earn a commission at no extra cost to you. Earning a commission never
          makes a link verified.
        </p>
        {/* Amazon Associates requires this statement, worded in our voice, wherever the site carries tagged Amazon links. */}
        {Boolean(affiliateLinks?.amazonHosts?.length) && (
          <p>As an Amazon Associate we earn from qualifying purchases.</p>
        )}
        <h3>What commissions never decide</h3>
        <p>
          Commissions don’t decide which fragrances we include, how we describe or rate them, or
          which comparisons are Recommended. A fragrance doesn’t need an affiliate link to be
          listed, and we don’t adjust comparisons to favour affiliate partners.
        </p>
      </section>
      <FAQ {...panel('faq')} />
      <aside className="about-closing" aria-labelledby="about-closing-title">
        <div>
          <h2 id="about-closing-title">Missing a fragrance?</h2>
          <p>Tell us about a fragrance we haven’t listed, or a comparison we should review.</p>
        </div>
        <div className="about-closing-actions">
          <Link to="/contact?kind=fragrance" className="button">
            Suggest a fragrance
          </Link>
          <Link to="/" className="text-link">
            Explore fragrances
          </Link>
        </div>
      </aside>
    </article>
  );
}

const faqItems = [
  {
    id: 'testing',
    question: 'Has Identifrag tested every fragrance itself?',
    answer:
      'No. We don’t claim first-hand wear testing of every fragrance. Performance notes summarise the evidence behind our editorial estimates; treat them as guidance rather than a personal wear test.',
  },
  {
    id: 'country-links',
    question: 'Why are there no buying links for my country?',
    answer:
      'Check that the delivery country in the navigation is right. If no links are listed, we don’t yet have a buying link for that destination. Some fragrances are listed for comparison only and have no buying links in any country. Confirm delivery with any retailer before ordering.',
  },
  {
    id: 'prices',
    question: 'Are prices and availability live?',
    answer:
      'No. Each price shows the date we checked it. The retailer confirms the final price, stock and delivery at checkout.',
  },
  {
    id: 'purchasing',
    question: 'Can I buy fragrances directly from Identifrag?',
    answer:
      'No. Buying links take you to the retailer, where you complete your purchase. The retailer handles payment, delivery and returns.',
  },
];
function FAQ(props: { hidden?: boolean; 'data-panel'?: string }) {
  return (
    <section className="about-panel faq-section" aria-labelledby="faq" {...props}>
      <h2 id="faq">Questions</h2>
      <div className="faq-list">
        {faqItems.map((item) => (
          <details className="faq-item" key={item.id}>
            <summary>
              {item.question}
              <ChevronDown size={19} aria-hidden />
            </summary>
            <div className="faq-answer" id={item.id}>
              {typeof item.answer === 'string' ? <p>{item.answer}</p> : item.answer}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
